
import {
  computed, defineComponent, PropType, ref, Ref, SetupContext, watch, nextTick,
} from 'vue';
import WidgetDataMetadata, { WidgetDataData } from '@/components/stats-builder/metadata/WidgetDataMetadata';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import Tooltip from 'primevue/tooltip';
import Dropdown from 'primevue/dropdown';
import { ChartType, StatsWidgetType } from '@/types/generated-types/graphql';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import MultiSelect from 'primevue/multiselect';
import SubPanelConfigContent from '@/components/automated-scenarios/SubPanelConfigContent.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import defaultLanguages from '@/configs/languages';
import StatsWidgetDataConfigOption from '@/components/stats-builder/widgets/form-config/StatsWidgetDataConfigOption.vue';
import CustomKpiWidgetDataConfig from '@/components/stats-builder/widgets/form-config/CustomKpiWidgetDataConfig.vue';
import type { CustomKpiSelection } from '@/components/stats-builder/widgets/form-config/CustomKpiWidgetDataConfig.vue';
import RawCustomDataTableWidgetDataConfig from '@/components/stats-builder/widgets/form-config/RawCustomDataTableWidgetDataConfig.vue';
import type {
  RawCustomDataSelection,
  RawCustomDataMeta,
} from '@/components/stats-builder/widgets/form-config/RawCustomDataTableWidgetDataConfig.vue';
import { resolveChannelSlugFromToken } from '@/constants/statsChannels';
import { fetchIsMultiSourceShop, isMetricAvailableForShop } from '@/composables/data-sources/multiSource';
import type {
  StatsPredefinedMetricCategory,
  StatsPredefinedMetricFilterOption,
  StatsPredefinedMetricFilters,
  StatsPredefinedMetricGroup,
  StatsPredefinedMetricOption,
} from '@/types/stats-predefined-metrics';

export default defineComponent({
  name: 'StatsWidgetDataConfig',
  components: {
    SubPanelConfigContent,
    FieldErrors,
    InputText,
    InputNumber,
    MultiSelect,
    Dropdown,
    LocalizedTextInput,
    StatsWidgetDataConfigOption,
    CustomKpiWidgetDataConfig,
    RawCustomDataTableWidgetDataConfig,
    Dialog,
    Button,
    InputSwitch,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: Object as PropType<WidgetDataData>,
      required: true,
      default() {
        return WidgetDataMetadata.Create();
      },
    },

    predefinedMetrics: {
      type: Array as PropType<StatsPredefinedMetricGroup[]>,
      required: false,
      default() {
        return [];
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: {
    modelValue: WidgetDataData;
    predefinedMetrics: StatsPredefinedMetricGroup[];
    formValidation: Record<string, any>;
  }, context: SetupContext) {
    const { t, te, locale } = useI18n();

    /* Listing to form errors */
    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);
    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;

    });

    // widget configuration
    const widgetName: Ref<any> = ref(props.modelValue.widgetName);

    // Convert widgetName to StringMap if current value is string
    if (typeof widgetName.value === 'string') {
      widgetName.value = Object.assign(
        {},
        ...defaultLanguages.map((lang) => ({ [lang]: widgetName.value })),
      );
    }

    const widgetConfiguration: Ref<any> = ref(props.modelValue.widgetConfiguration);
    const useDefaultConfigurationForWidget = ref(!widgetConfiguration.value);
    const includeTotal = ref(Boolean(widgetConfiguration.value?.includeTotal));
    const totalOnly = ref(Boolean(widgetConfiguration.value?.totalOnly));

    if (!includeTotal.value && totalOnly.value) {
      totalOnly.value = false;
    }

    // ===== Custom KPI : refs et helpers dédiés (bypass du flow natif) =====
    const customSelection = ref<CustomKpiSelection>(
      widgetConfiguration.value?.customConfig
        && (widgetConfiguration.value.customConfig as any).measures !== undefined
        ? { ...(widgetConfiguration.value.customConfig as any) }
        : { measures: [], dimensions: [] },
    );

    // ===== Tableau brut sur data custom : refs et helpers dédiés =====
    const rawCustomSelection = ref<RawCustomDataSelection>(
      widgetConfiguration.value?.customConfig
        && (widgetConfiguration.value.customConfig as any).selectedColumns !== undefined
        ? { ...(widgetConfiguration.value.customConfig as any) }
        : { selectedColumns: [], enabledRelations: [], userFilters: [] },
    );

    // Selected metric (if widget edition)
    const selectedMetric = ref<StatsPredefinedMetricOption>();

    interface FlattenedMetricEntry {
      groupKey: string;
      groupLabel: string;
      categoryKey: string;
      categoryLabel: string;
      data: StatsPredefinedMetricOption;
    }

    // metric widget type
    const listOfMetricWidgetTypes = computed<StatsWidgetType[]>(() => (selectedMetric.value
      ? Object.keys(selectedMetric.value.value) as StatsWidgetType[]
      : []));
    const selectedMetricWidgetType: Ref<StatsWidgetType | null> = ref(null);

    // ===== Custom KPI : computed pour détection + options =====
    const isCustomKpi = computed(() => {
      if (selectedMetric.value?.group !== 'custom') return false;
      if (!selectedMetricWidgetType.value) return false;
      const widgetCfg = (selectedMetric.value.value as any)?.[selectedMetricWidgetType.value];
      // Discrimine vs Tableau brut : seul un KPI custom expose .custom
      // (la sentinelle .rawCustomData est exclusive aux Tables brutes).
      return !!widgetCfg?.custom && !widgetCfg?.rawCustomData;
    });

    const isRawCustomData = computed(() => {
      if (selectedMetric.value?.group !== 'custom') return false;
      if (!selectedMetricWidgetType.value) return false;
      const widgetCfg = (selectedMetric.value.value as any)?.[selectedMetricWidgetType.value];
      return !!widgetCfg?.rawCustomData;
    });

    const customMeta = computed(() => {
      if (!isCustomKpi.value || !selectedMetric.value || !selectedMetricWidgetType.value) return null;
      const widgetCfg = (selectedMetric.value.value as any)?.[selectedMetricWidgetType.value];
      return widgetCfg?.custom ?? null;
    });

    const rawCustomMeta = computed<RawCustomDataMeta | null>(() => {
      if (!isRawCustomData.value || !selectedMetric.value || !selectedMetricWidgetType.value) return null;
      const widgetCfg = (selectedMetric.value.value as any)?.[selectedMetricWidgetType.value];
      return widgetCfg?.rawCustomData ?? null;
    });

    const onCustomSelectionUpdate = (next: CustomKpiSelection) => {
      customSelection.value = { ...next };
    };

    const onRawCustomSelectionUpdate = (next: RawCustomDataSelection) => {
      rawCustomSelection.value = { ...next };
    };

    /**
     * Détecte si une metric est un Tableau brut auto-généré sur une data
     * custom definition (pour afficher le badge dans le picker).
     */
    const isRawCustomDataMetric = (metric: any): boolean => {
      if (!metric || metric.group !== 'custom') return false;
      const types = metric.value ?? {};
      return Object.values(types).some((v: any) => v?.rawCustomData);
    };

    const customMeasuresOptions = computed(() => customMeta.value?.measures ?? []);
    const customDimensionsOptions = computed(() => customMeta.value?.dimensions ?? []);
    const customSupportsTimeline = computed(
      () => customMeta.value?.supportsTimeline !== false,
    );

    // Initialise une sélection custom par défaut quand on entre sur un custom KPI sans selection.
    watch(
      [isCustomKpi, customMeta],
      ([isCustom, meta]) => {
        if (!isCustom || !meta) return;
        if (!customSelection.value.measures || customSelection.value.measures.length === 0) {
          if (meta.measures.length > 0) {
            customSelection.value = {
              ...customSelection.value,
              measures: [meta.measures[0].key],
            };
          }
        }
        if (selectedMetricWidgetType.value === 'chart' && !customSelection.value.chartMode) {
          customSelection.value = {
            ...customSelection.value,
            chartMode: customSupportsTimeline.value ? 'timeline' : 'distribution',
            chartSubType: customSupportsTimeline.value ? 'bar' : 'pie',
          };
        }
      },
      { immediate: true },
    );

    // Initialise la sélection raw custom data avec defaults dès qu'on entre
    // sur un widget Tableau brut sans config préalable.
    watch(
      [isRawCustomData, rawCustomMeta],
      ([isRaw, meta]) => {
        if (!isRaw || !meta) return;
        if (
          !rawCustomSelection.value.selectedColumns
          || rawCustomSelection.value.selectedColumns.length === 0
        ) {
          rawCustomSelection.value = {
            selectedColumns: [...(meta.defaultColumns ?? [])],
            enabledRelations: rawCustomSelection.value.enabledRelations ?? [],
            userFilters: rawCustomSelection.value.userFilters ?? [],
            dateColumn: rawCustomSelection.value.dateColumn ?? meta.defaultDateColumn,
          };
        }
      },
      { immediate: true },
    );

    // if selectedMetricWidgetType === CHART => we define chart categories and type
    type RepartitionVariantMeta = {
      dimension?: string;
      measure?: string;
      dimensionLabelKey?: string;
      measureLabelKey?: string;
    };
    type ChartCategoryOption = {
      name: string;
      code: string;
      charts: Record<string, StatsPredefinedMetricFilters>;
      // Système v3 (games) : métadonnées de répartition optionnelles, hors
      // de `charts` pour ne pas polluer les Object.keys(charts) du legacy.
      charts_meta?: RepartitionVariantMeta;
      hidden?: boolean;
    };

    const listOfChartCategories = ref<ChartCategoryOption[]>([]);
    const chartCategoryGroups: Ref<Record<string, { label?: string; labelKey?: string; variants?: string[] }>> = ref({});
    const metricsWithLegacyGlobalCategory = ['bulk-sending', 'automation-sending', 'automation-deliveries'];

    const normalizeLegacyCategoryCode = (metricKey: string | undefined, categoryCode: string) => {
      let normalized = categoryCode;

      if (metricKey && metricsWithLegacyGlobalCategory.includes(metricKey) && categoryCode === 'all') {
        normalized = 'channels';
      }

      const slug = resolveChannelSlugFromToken(normalized);
      return slug ?? normalized;
    };
    const listOfChartCategoriesIsMultiple: Ref<boolean> = ref(false);
    const selectedChartCategory = ref<ChartCategoryOption[]>([]);

    const selectedChartTypeByCat = ref<Record<string, string>>({});
    const customizeChartTypePerCategory = ref(false);
    const globalChartType = ref('');

    type ChartDisplayMode = 'timeline' | 'repartition';

    type ChartTypeOption = { key: ChartType; label: string; icon: string };

    const timelineChartTypes: ChartType[] = [ChartType.LINE, ChartType.BAR, ChartType.SCATTER];
    const repartitionChartTypes: ChartType[] = [ChartType.PIE, ChartType.DOUGHNUT, ChartType.RADAR, ChartType.POLARAREA];

    const selectedChartDisplayMode = ref<ChartDisplayMode | null>(null);
    // Système de filtres généralisé
    const selectedFiltersByCat = ref<Record<string, Record<string, StatsPredefinedMetricFilterOption[]>>>({});

    const globalTimelineFiltersSelection = ref<Record<string, StatsPredefinedMetricFilterOption[]>>({});

    // Variables legacy pour compatibilité (seront supprimées progressivement)
    const selectedChannelFiltersByCat = ref<Record<string, StatsPredefinedMetricFilterOption[]>>({});
    const selectedTypeFiltersByCat = ref<Record<string, StatsPredefinedMetricFilterOption[]>>({});
    const selectedBounceTypeFiltersByCat = ref<Record<string, StatsPredefinedMetricFilterOption[]>>({});

    const chartConfigPanel = computed(() => ([
      {
        name: 'chart_config',
        label: 'statsEditor.widgets.widgetData.config.charts.title',
        show: true,
      },
    ]));

    type ChartSubConfigPanel = {
      name: string;
      label: string;
      code: string;
      charts: Record<string, StatsPredefinedMetricFilters>;
      originalIndex: number;
      show: boolean;
      resolvedChartType: string | null;
    };

    const resolveChartTypeForCategory = (category: ChartCategoryOption): string => {
      const explicitChartType = selectedChartTypeByCat.value[category.code];
      if (explicitChartType) {
        return explicitChartType;
      }

      if (selectedChartDisplayMode.value === 'timeline') {
        if (!customizeChartTypePerCategory.value && globalChartType.value) {
          return globalChartType.value;
        }

        const availableTimelineTypes = getTimelineChartTypesForCategory(category);
        if (availableTimelineTypes.length === 1) {
          return availableTimelineTypes[0];
        }
      }

      if (selectedChartDisplayMode.value === 'repartition') {
        // En mode répartition, utiliser le type global sélectionné
        if (repartitionChartType.value) {
          return repartitionChartType.value;
        }

        // Sinon, retourner le premier type disponible pour cette catégorie
        const availableRepartitionTypes = Object.keys(category.charts)
          .filter((chartType) => repartitionChartTypes.includes(chartType as ChartType));
        if (availableRepartitionTypes.length > 0) {
          return availableRepartitionTypes[0];
        }
      }

      return '';
    };

    const isTimelineGlobalMode = computed(() => selectedChartDisplayMode.value === 'timeline'
      && !customizeChartTypePerCategory.value);

    const chartSubConfigPanels = computed<ChartSubConfigPanel[]>(() => selectedChartCategory.value.map((cat, index) => {
      const resolvedChartType = resolveChartTypeForCategory(cat);

      const availableFilters = resolvedChartType
        ? getAvailableFiltersForChart(cat.code, resolvedChartType)
        : {};

      const panelFilters = Object.entries(availableFilters)
        .filter(([, options]) => Array.isArray(options) && options.length > 0);
      const hasVisibleFilters = panelFilters.length > 0;

      let shouldDisplayFilters = hasVisibleFilters;

      if (shouldDisplayFilters && isTimelineGlobalMode.value) {
        const globalFilterKeys = new Set(globalTimelineFilterOptions.value.map((option) => option.key));
        shouldDisplayFilters = panelFilters.some(([filterKey]) => !globalFilterKeys.has(filterKey));
      }

      const hasPerItemChartSelection = selectedChartDisplayMode.value === 'timeline'
        && customizeChartTypePerCategory.value
        && Object.keys(cat.charts).length > 1;

      return {
        name: `chart_subConfig_${cat.code}`,
        label: cat.name,
        code: cat.code,
        charts: cat.charts,
        originalIndex: index,
        show: selectedChartDisplayMode.value === 'timeline'
          ? hasPerItemChartSelection || shouldDisplayFilters
          : shouldDisplayFilters,
        resolvedChartType: resolvedChartType || null,
      };
    }));

    const visibleChartSubConfigPanels = computed(() => chartSubConfigPanels.value.filter((panel) => panel.show));

    const hasModeForCategory = (category: ChartCategoryOption, mode: ChartDisplayMode) => {
      const availableTypes = Object.keys(category.charts) as ChartType[];
      const allowedTypes = mode === 'timeline' ? timelineChartTypes : repartitionChartTypes;
      return availableTypes.some((type) => allowedTypes.includes(type));
    };

    const filterCategoryChartsByMode = (category: ChartCategoryOption, mode: ChartDisplayMode) => {
      const allowedTypes = mode === 'timeline' ? timelineChartTypes : repartitionChartTypes;
      return Object.entries(category.charts).reduce<Record<string, StatsPredefinedMetricFilters>>((acc, [chartType, filters]) => {
        if (allowedTypes.includes(chartType as ChartType)) {
          acc[chartType] = filters;
        }
        return acc;
      }, {});
    };

    const getTimelineChartTypesForCategory = (category: ChartCategoryOption): ChartType[] => Object.keys(category.charts)
      .filter((chartType) => timelineChartTypes.includes(chartType as ChartType)) as ChartType[];

    const isRepartitionMode = computed(() => selectedChartDisplayMode.value === 'repartition');

    const availableChartCategories = computed<ChartCategoryOption[]>(() => {
      if (!selectedChartDisplayMode.value) {
        return listOfChartCategories.value;
      }

      const categories = listOfChartCategories.value.reduce<ChartCategoryOption[]>((acc, category) => {
        if (!hasModeForCategory(category, selectedChartDisplayMode.value as ChartDisplayMode)) {
          return acc;
        }

        acc.push({
          ...category,
          charts: filterCategoryChartsByMode(category, selectedChartDisplayMode.value as ChartDisplayMode),
        });

        return acc;
      }, []);

      if (selectedChartDisplayMode.value === 'repartition') {
        const selectedDimension = selectedRepartitionDimensionKey.value;

        const selectedCodes = new Set(selectedChartCategory.value.map((category) => category.code));
        const filteredCategories = categories.filter((category) => !category.hidden || selectedCodes.has(category.code));

        // Vérifier si selectedDimension est un groupe ou une catégorie directe
        const isGroup = selectedDimension ? !!chartCategoryGroups.value[selectedDimension] : false;
        const dimensionVariants = (isGroup && selectedDimension)
          ? new Set(chartCategoryGroups.value[selectedDimension]?.variants ?? [])
          : null;

        // Si c'est un groupe, filtrer par ses variantes
        if (dimensionVariants && dimensionVariants.size) {
          return filteredCategories.filter((category) => dimensionVariants.has(category.code));
        }

        // Si c'est une catégorie directe, retourner uniquement cette catégorie
        if (selectedDimension && !isGroup) {
          return filteredCategories.filter((category) => category.code === selectedDimension);
        }

        return filteredCategories;
      }

      return categories;
    });

    const categoriesForTimelineSelection = computed<ChartCategoryOption[]>(() => (selectedChartCategory.value.length
      ? selectedChartCategory.value
      : availableChartCategories.value));

    const globalTimelineChartTypeOptions = computed<ChartTypeOption[]>(() => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        return [];
      }

      const categories = categoriesForTimelineSelection.value;

      if (!categories.length) {
        return [];
      }

      const availableTypes = categories.reduce<ChartType[]>((acc, category, index) => {
        const categoryTypes = getTimelineChartTypesForCategory(category);

        if (index === 0) {
          return categoryTypes;
        }

        return acc.filter((type) => categoryTypes.includes(type));
      }, [] as ChartType[]);

      return availableTypes.map<ChartTypeOption>((type) => ({
        key: type,
        label: t(`statsEditor.widgets.widgetData.config.charts.type.lists.label.${type}`),
        icon: t(`statsEditor.widgets.widgetData.config.charts.type.lists.icons.${type}`),
      }));
    });

    type TimelineFilterOptionGroup = {
      key: string;
      options: StatsPredefinedMetricFilterOption[];
    };

    const globalTimelineFilterOptions = computed<TimelineFilterOptionGroup[]>(() => {
      if (!isTimelineGlobalMode.value) {
        return [];
      }

      const categories = categoriesForTimelineSelection.value;
      if (!categories.length) {
        return [];
      }

      const filtersByCategory = categories.map((category) => {
        const chartType = resolveChartTypeForCategory(category);
        if (!chartType) {
          return {} as Record<string, StatsPredefinedMetricFilterOption[]>;
        }

        return getAvailableFiltersForChart(category.code, chartType);
      });

      if (!filtersByCategory.length) {
        return [];
      }

      const sharedKeys = filtersByCategory.reduce<Set<string>>((acc, filters, index) => {
        const keys = Object.entries(filters)
          .filter(([, value]) => Array.isArray(value) && value.length > 0)
          .map(([key]) => key);

        if (index === 0) {
          return new Set(keys);
        }

        return new Set(keys.filter((key) => acc.has(key)));
      }, new Set<string>());

      if (!sharedKeys.size) {
        return [];
      }

      const [referenceFilters] = filtersByCategory;

      return Array.from(sharedKeys).map((key) => ({
        key,
        options: referenceFilters[key] ?? [],
      }));
    });

    const hasMultipleTimelineTypesAvailable = computed(() => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        return false;
      }

      return categoriesForTimelineSelection.value
        .some((category) => getTimelineChartTypesForCategory(category).length > 1);
    });

    const shouldShowTimelineTypeToggle = computed(() => selectedChartDisplayMode.value === 'timeline'
      && categoriesForTimelineSelection.value.length > 0
      && hasMultipleTimelineTypesAvailable.value);

    const shouldShowGlobalChartTypeSelector = computed(() => selectedChartDisplayMode.value === 'timeline'
      && !customizeChartTypePerCategory.value
      && globalTimelineChartTypeOptions.value.length > 0);

    const shouldShowGlobalTimelineFilters = computed(() => isTimelineGlobalMode.value
      && globalTimelineFilterOptions.value.length > 0);

    const chartDisplayModeOptions = computed(() => {
      const modes: { key: ChartDisplayMode; label: string; icon: string; disabled: boolean }[] = [
        {
          key: 'timeline',
          label: t('statsEditor.widgets.widgetData.config.charts.displayMode.timeline'),
          icon: 'fa-regular fa-chart-line',
          disabled: !listOfChartCategories.value.some((category) => hasModeForCategory(category, 'timeline')),
        },
        {
          key: 'repartition',
          label: t('statsEditor.widgets.widgetData.config.charts.displayMode.repartition'),
          icon: 'fa-regular fa-chart-pie',
          disabled: !listOfChartCategories.value.some((category) => hasModeForCategory(category, 'repartition')),
        },
      ];

      return modes;
    });

    const areFilterSelectionsEqual = (
      first: StatsPredefinedMetricFilterOption[],
      second: StatsPredefinedMetricFilterOption[],
    ): boolean => {
      if (first.length !== second.length) {
        return false;
      }

      return first.every((item, index) => item?.code === second[index]?.code);
    };

    const hasGlobalSelectionChanged = (
      current: Record<string, StatsPredefinedMetricFilterOption[]>,
      next: Record<string, StatsPredefinedMetricFilterOption[]>,
    ): boolean => {
      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(next);

      if (currentKeys.length !== nextKeys.length) {
        return true;
      }

      const sortedCurrent = [...currentKeys].sort();
      const sortedNext = [...nextKeys].sort();

      if (sortedCurrent.some((key, index) => key !== sortedNext[index])) {
        return true;
      }

      return sortedNext.some((key) => {
        const currentSelection = current[key] ?? [];
        const nextSelection = next[key] ?? [];

        return !areFilterSelectionsEqual(currentSelection, nextSelection);
      });
    };

    const getPreferredSelectionForFilter = (filterKey: string): StatsPredefinedMetricFilterOption[] => {
      const categories = selectedChartCategory.value;
      for (let index = 0; index < categories.length; index += 1) {
        const category = categories[index];
        const selection = selectedFiltersByCat.value[category.code]?.[filterKey];
        if (selection && selection.length) {
          return selection;
        }
      }

      return globalTimelineFiltersSelection.value[filterKey]
        ? [...globalTimelineFiltersSelection.value[filterKey]]
        : [];
    };

    const syncGlobalTimelineFiltersFromCategories = () => {
      if (!isTimelineGlobalMode.value) {
        return;
      }

      const optionKeys = globalTimelineFilterOptions.value.map((option) => option.key);
      if (!optionKeys.length || !selectedChartCategory.value.length) {
        if (Object.keys(globalTimelineFiltersSelection.value).length) {
          globalTimelineFiltersSelection.value = {};
        }
        return;
      }

      const nextSelection = optionKeys.reduce<Record<string, StatsPredefinedMetricFilterOption[]>>((acc, filterKey) => {
        acc[filterKey] = getPreferredSelectionForFilter(filterKey).slice();
        return acc;
      }, {});

      if (hasGlobalSelectionChanged(globalTimelineFiltersSelection.value, nextSelection)) {
        globalTimelineFiltersSelection.value = nextSelection;
      }
    };

    const applyGlobalTimelineFiltersToCategories = () => {
      if (!isTimelineGlobalMode.value) {
        return;
      }

      const optionKeys = new Set(globalTimelineFilterOptions.value.map((option) => option.key));
      if (!optionKeys.size) {
        return;
      }

      selectedChartCategory.value.forEach((category) => {
        if (!selectedFiltersByCat.value[category.code]) {
          selectedFiltersByCat.value[category.code] = {};
        }

        const categoryFilters = selectedFiltersByCat.value[category.code];
        Object.keys(categoryFilters).forEach((filterKey) => {
          if (!optionKeys.has(filterKey)) {
            delete categoryFilters[filterKey];
          }
        });

        optionKeys.forEach((filterKey) => {
          const selection = globalTimelineFiltersSelection.value[filterKey] ?? [];
          categoryFilters[filterKey] = selection.slice();
        });
      });
    };

    const chartCategoriesLabel = computed(() => {
      if (isRepartitionMode.value) {
        return t('statsEditor.widgets.widgetData.config.charts.categories.repartitionDimensionLabel');
      }

      return t(`statsEditor.widgets.widgetData.config.charts.categories.label${listOfChartCategoriesIsMultiple.value ? 'Multiple' : ''}`);
    });

    const chartCategoriesPlaceholder = computed(() => {
      if (isRepartitionMode.value) {
        return t('statsEditor.widgets.widgetData.config.charts.categories.repartitionDimensionPlaceholder');
      }

      return t(`statsEditor.widgets.widgetData.config.charts.categories.placeholder${listOfChartCategoriesIsMultiple.value ? 'Multiple' : ''}`);
    });

    const repartitionMetricLabel = computed(() => t('statsEditor.widgets.widgetData.config.charts.categories.repartitionMetricLabel'));

    const repartitionMetricPlaceholder = computed(() => t('statsEditor.widgets.widgetData.config.charts.categories.repartitionMetricPlaceholder'));

    const selectedRepartitionDimensionKey = ref<string | null>(null);
    const selectedRepartitionMetricKey = ref<string | null>(null);

    const hasRepartitionGroups = computed(() => isRepartitionMode.value && Object.keys(chartCategoryGroups.value).length > 0);

    type RepartitionMetricFamily = 'carts' | 'orders' | 'traffic' | 'games';

    type RepartitionMetricMapping = {
      suffix: string;
      key: string;
      family?: RepartitionMetricFamily;
      // Système v3 (games) : si présent, le front affiche directement
      // ce label i18n. Sinon fallback sur la convention de nommage
      // (`repartitionMetricOptions.<family>.<key>`) du système legacy.
      labelKey?: string;
    };

    const repartitionMetricMappings: RepartitionMetricMapping[] = [
      { suffix: 'CartVoucherCount', key: 'voucherCount', family: 'carts' },
      { suffix: 'CartVoucherAmount', key: 'voucherAmount', family: 'carts' },
      { suffix: 'CartCount', key: 'count', family: 'carts' },
      { suffix: 'CartAmount', key: 'amount', family: 'carts' },
      { suffix: 'OrderVoucherSpmCount', key: 'voucherSpmCount', family: 'orders' },
      { suffix: 'OrderVoucherSpmAmount', key: 'voucherSpmAmount', family: 'orders' },
      { suffix: 'OrderVoucherCount', key: 'voucherCount', family: 'orders' },
      { suffix: 'OrderVoucherAmount', key: 'voucherAmount', family: 'orders' },
      { suffix: 'OrderDecisionTime', key: 'decisionTime', family: 'orders' },
      { suffix: 'OrderCount', key: 'count', family: 'orders' },
      { suffix: 'OrderAmount', key: 'amount', family: 'orders' },
      { suffix: 'Sessions', key: 'sessions', family: 'traffic' },
      { suffix: 'SingleVisits', key: 'singleVisits', family: 'traffic' },
      { suffix: 'ViewedPages', key: 'viewedPages', family: 'traffic' },
      { suffix: 'AverageSessionTime', key: 'averageSessionTime', family: 'traffic' },
    ];

    // Lit les métadonnées de répartition d'un variant (système v3 — games).
    // Stockées dans category.charts_meta, hors de category.charts pour ne pas
    // polluer les Object.keys(charts) des itérations legacy.
    // Retourne null si absent → caller fallback sur le suffix-parsing legacy.
    const getVariantRepartitionMetadata = (variantCode: string | undefined | null) => {
      if (!variantCode) return null;
      const category = listOfChartCategories.value.find((c) => c.code === variantCode);
      const meta = category?.charts_meta;
      if (meta?.dimension && meta?.measure) {
        return {
          dimension: meta.dimension,
          measure: meta.measure,
          dimensionLabelKey: meta.dimensionLabelKey,
          measureLabelKey: meta.measureLabelKey,
        };
      }
      return null;
    };

    const findRepartitionMetricMapping = (variantCode: string | undefined | null) => {
      // Path v3 : métadonnées explicites dans le variant.
      const meta = getVariantRepartitionMetadata(variantCode);
      if (meta) {
        const v3Mapping: RepartitionMetricMapping = {
          suffix: '',
          key: meta.measure,
          family: 'games',
          labelKey: meta.measureLabelKey,
        };
        return v3Mapping;
      }
      // Path legacy : suffix-based (orders, carts, traffic).
      return repartitionMetricMappings.find(({ suffix }) => variantCode?.endsWith(suffix));
    };

    const getMetricKeyFromVariant = (variantCode: string | undefined | null) => {
      const mapping = findRepartitionMetricMapping(variantCode);
      return mapping?.key ?? null;
    };

    const getMetricLabelFromKey = (
      metricKey: string,
      family: RepartitionMetricFamily | null = null,
      explicitLabelKey?: string,
    ) => {
      // Priorité absolue : labelKey explicite (système v3 games).
      if (explicitLabelKey && te(explicitLabelKey)) {
        return t(explicitLabelKey);
      }
      const translationKeys = [
        family
          ? `statsEditor.widgets.widgetData.config.charts.categories.repartitionMetricOptions.${family}.${metricKey}`
          : null,
        `statsEditor.widgets.widgetData.config.charts.categories.repartitionMetricOptions.${metricKey}`,
      ].filter(Boolean) as string[];

      const matchingKey = translationKeys.find((key) => te(key));
      return matchingKey ? t(matchingKey) : metricKey;
    };

    const repartitionDimensionOptions = computed(() => {
      if (!isRepartitionMode.value) {
        return [] as Array<{ code: string; name: string }>;
      }

      const availableCodes = new Set(listOfChartCategories.value.map((category) => category.code));
      const repartitionCategories = listOfChartCategories.value.filter((cat) => hasModeForCategory(cat, 'repartition'));

      // Si un groupe n'a qu'une seule métrique unique, on déplie ses variantes
      // Sinon on garde le groupe (pour les cas comme Commandes avec plusieurs métriques par groupe)
      const groupedOptions = Object.entries(chartCategoryGroups.value).reduce<Array<{ code: string; name: string }>>(
        (acc, [groupKey, groupValue]) => {
          const variants = (groupValue?.variants ?? []).filter((variant) => availableCodes.has(variant));

          if (!variants.length) {
            return acc;
          }

          // Vérifier combien de métriques uniques ce groupe contient
          const uniqueMetrics = new Set(
            variants.map(variant => getMetricKeyFromVariant(variant)).filter(Boolean)
          );

          // Si une seule métrique (ou aucune), déplier les variantes directement
          if (uniqueMetrics.size <= 1) {
            variants.forEach((variantCode) => {
              const category = repartitionCategories.find((cat) => cat.code === variantCode);
              if (category) {
                acc.push({
                  code: category.code,
                  name: category.name,
                });
              }
            });
          } else {
            // Sinon, garder le groupe.
            // Priorité : labelKey i18n explicite (système v3 games),
            // sinon résolution legacy via getChartCategoryLabel,
            // sinon label en dur du groupe (fallback).
            const explicitLabelKey = (groupValue as any)?.labelKey;
            let name: string;
            if (explicitLabelKey && te(explicitLabelKey)) {
              name = t(explicitLabelKey);
            } else {
              const translatedLabel = getChartCategoryLabel(groupKey, selectedMetric.value);
              name = translatedLabel !== groupKey ? translatedLabel : groupValue?.label ?? translatedLabel;
            }

            acc.push({
              code: groupKey,
              name,
            });
          }

          return acc;
        },
        [],
      );

      if (groupedOptions.length) {
        return groupedOptions;
      }

      return repartitionCategories.map((category) => ({
        code: category.code,
        name: category.name,
      }));
    });

    const repartitionMetricOptions = computed(() => {
      if (!isRepartitionMode.value) {
        return [] as Array<{ code: string; name: string }>;
      }

      const selectedDimension = selectedRepartitionDimensionKey.value;
      if (!selectedDimension) {
        return [] as Array<{ code: string; name: string }>;
      }

      const availableVariants = chartCategoryGroups.value[selectedDimension]?.variants ?? [];
      const allowedCategoryCodes = new Set(listOfChartCategories.value.map((category) => category.code));

      const metricFamilies: Record<string, RepartitionMetricFamily | null> = {};
      const metricLabelKeys: Record<string, string | undefined> = {};

      const metricKeys = availableVariants.reduce<string[]>((acc, variantCode) => {
        if (!allowedCategoryCodes.has(variantCode)) {
          return acc;
        }

        const mapping = findRepartitionMetricMapping(variantCode);
        const metricKey = mapping?.key ?? null;
        if (metricKey && !acc.includes(metricKey)) {
          acc.push(metricKey);
        }

        if (metricKey && !metricFamilies[metricKey]) {
          metricFamilies[metricKey] = mapping?.family ?? null;
        }
        if (metricKey && !metricLabelKeys[metricKey] && mapping?.labelKey) {
          metricLabelKeys[metricKey] = mapping.labelKey;
        }

        return acc;
      }, []);

      return metricKeys.map((code) => ({
        code,
        name: getMetricLabelFromKey(code, metricFamilies[code] ?? null, metricLabelKeys[code]),
      }));
    });

    const shouldUseRepartitionMetricSelector = computed(
      () => hasRepartitionGroups.value && repartitionMetricOptions.value.length > 0,
    );

    const applyRepartitionSelection = () => {
      if (!isRepartitionMode.value) {
        return;
      }

      const dimension = selectedRepartitionDimensionKey.value;
      const metricKey = selectedRepartitionMetricKey.value;

      if (!shouldUseRepartitionMetricSelector.value) {
        const categories = availableChartCategories.value;
        const targetCategory =
          categories.find((category) => category.code === dimension)
          ?? categories[0]
          ?? null;

        const currentCode = selectedChartCategory.value[0]?.code ?? null;
        const nextCode = targetCategory?.code ?? null;

        if (currentCode !== nextCode) {
          selectedChartCategory.value = targetCategory ? [targetCategory] : [];
        }

        return;
      }

      if (!dimension || !metricKey) {
        if (selectedChartCategory.value.length) {
          selectedChartCategory.value = [];
        }
        return;
      }

      // Vérifier si dimension est un groupe ou une catégorie directe
      const isGroup = dimension ? !!chartCategoryGroups.value[dimension] : false;
      const allowedCategories = availableChartCategories.value;

      if (!isGroup) {
        // Si c'est une catégorie directe (comme "age"), la sélectionner directement
        const targetCategory = allowedCategories.find((category) => category.code === dimension);

        if (targetCategory) {
          const currentCategoryCode = selectedChartCategory.value[0]?.code ?? null;
          if (currentCategoryCode !== targetCategory.code) {
            selectedChartCategory.value = [targetCategory];
          }
        }
        return;
      }

      // Si c'est un groupe (comme "status"), chercher la bonne variante
      const availableVariants = chartCategoryGroups.value[dimension]?.variants ?? [];

      const matchingVariant = availableVariants.find((variant) => {
        if (!allowedCategories.some((category) => category.code === variant)) {
          return false;
        }

        return getMetricKeyFromVariant(variant) === metricKey;
      });

      const fallbackVariant = matchingVariant
        ? null
        : availableVariants.find((variant) => allowedCategories.some((category) => category.code === variant));

      const variantToUse = matchingVariant ?? fallbackVariant ?? null;

      if (!variantToUse) {
        if (selectedChartCategory.value.length) {
          selectedChartCategory.value = [];
        }
        return;
      }

      if (!matchingVariant) {
        const fallbackMetricKey = getMetricKeyFromVariant(variantToUse);
        if (fallbackMetricKey) {
          selectedRepartitionMetricKey.value = fallbackMetricKey;
        }
      }

      const selectedCategory = allowedCategories.find((category) => category.code === variantToUse);
      const currentCategoryCode = selectedChartCategory.value[0]?.code ?? null;
      const nextCategoryCode = selectedCategory?.code ?? null;

      if (currentCategoryCode !== nextCategoryCode) {
        selectedChartCategory.value = selectedCategory ? [selectedCategory] : [];
      }
    };

    const repartitionChartType = ref('');

    // Limitation du nombre de tranches pour les graphiques de répartition
    const limitSlices = ref(Boolean(widgetConfiguration.value?.maxSlices));
    const maxSlices = ref(widgetConfiguration.value?.maxSlices ?? 25);

    const repartitionChartTypeOptions = computed<ChartTypeOption[]>(() => {
      if (selectedChartDisplayMode.value !== 'repartition') {
        return [] as ChartTypeOption[];
      }

      const categories = (selectedChartCategory.value.length
        ? selectedChartCategory.value
        : availableChartCategories.value);

      if (!categories.length) {
        return [] as ChartTypeOption[];
      }

      const availableTypes = categories.reduce<ChartType[]>((acc, category, index) => {
        const categoryTypes = Object.keys(category.charts)
          .filter((chartType) => repartitionChartTypes.includes(chartType as ChartType)) as ChartType[];

        if (index === 0) {
          return categoryTypes;
        }

        return acc.filter((type) => categoryTypes.includes(type));
      }, [] as ChartType[]);

      return availableTypes.map<ChartTypeOption>((type) => ({
        key: type,
        label: t(`statsEditor.widgets.widgetData.config.charts.type.lists.label.${type}`),
        icon: t(`statsEditor.widgets.widgetData.config.charts.type.lists.icons.${type}`),
      }));
    });

    const clearChartSelections = () => {
      selectedChartCategory.value = [];
      selectedChartTypeByCat.value = {};
      selectedFiltersByCat.value = {};
      selectedChannelFiltersByCat.value = {};
      selectedTypeFiltersByCat.value = {};
      selectedBounceTypeFiltersByCat.value = {};
      repartitionChartType.value = '';
      selectedRepartitionDimensionKey.value = null;
      selectedRepartitionMetricKey.value = null;
      customizeChartTypePerCategory.value = false;
      globalChartType.value = '';
    };

    const selectChartDisplayMode = (mode: ChartDisplayMode) => {
      const option = chartDisplayModeOptions.value.find((item) => item.key === mode);
      if (!option || option.disabled) {
        return;
      }

      if (selectedChartDisplayMode.value === mode) {
        return;
      }

      clearChartSelections();
      selectedChartDisplayMode.value = mode;
      setDefaultConfigurationForWidget();
    };

    const selectRepartitionChartType = (type: string) => {
      if (repartitionChartType.value === type) {
        return;
      }

      const availableTypes = repartitionChartTypeOptions.value.map(({ key }: ChartTypeOption) => key);
      if (!availableTypes.includes(type as ChartType)) {
        return;
      }

      repartitionChartType.value = type;
    };

    const ensureGlobalChartTypeIsValid = () => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        globalChartType.value = '';
        return;
      }

      const options = globalTimelineChartTypeOptions.value;

      if (!options.length) {
        globalChartType.value = '';
        return;
      }

      if (!options.some((option) => option.key === globalChartType.value)) {
        globalChartType.value = options[0].key;
      }
    };

    const applyGlobalChartTypeToCategories = () => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        return;
      }

      ensureGlobalChartTypeIsValid();

      if (!globalChartType.value) {
        return;
      }

      selectedChartCategory.value.forEach((category) => {
        selectedChartTypeByCat.value[category.code] = globalChartType.value;
      });
    };

    const synchronizeGlobalChartType = () => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        return;
      }

      if (!globalChartType.value) {
        const firstSelectedType = selectedChartCategory.value.reduce<string | null>((acc, category) => {
          if (acc) {
            return acc;
          }

          const categoryType = selectedChartTypeByCat.value[category.code];
          if (categoryType) {
            return categoryType;
          }

          const availableTypes = getTimelineChartTypesForCategory(category);
          return availableTypes[0] ?? null;
        }, null);

        if (firstSelectedType) {
          globalChartType.value = firstSelectedType;
        }
      }

      applyGlobalChartTypeToCategories();
    };

    const selectGlobalChartType = (type: string) => {
      if (globalChartType.value === type) {
        return;
      }

      if (!globalTimelineChartTypeOptions.value.some((option) => option.key === type)) {
        return;
      }

      globalChartType.value = type;
      applyGlobalChartTypeToCategories();
    };

    const selectedMetricFilters = computed<StatsPredefinedMetricFilters | null>(() => {
      if (!selectedMetric.value || !selectedMetricWidgetType.value) {
        return null;
      }

      if (selectedMetricWidgetType.value === StatsWidgetType.CHART) {
        return null;
      }

      const filters = selectedMetric.value.value[selectedMetricWidgetType.value] as StatsPredefinedMetricFilters | undefined;

      return filters ?? null;
    });

    const metricHasSegmentableFilters = computed(() => {
      if (!selectedMetricFilters.value) {
        return false;
      }

      return Object.values(selectedMetricFilters.value)
        .some((options) => Array.isArray(options) && options.length > 0);
    });

    const metricSupportsTotals = computed(() => selectedMetric.value?.supportsTotals !== false);

    const canIncludeTotal = computed(() => selectedMetricWidgetType.value === StatsWidgetType.KPI
      && metricSupportsTotals.value
      && metricHasSegmentableFilters.value);

    // Système de filtres généralisé pour KPI et Table
    const selectedFilters = ref<Record<string, StatsPredefinedMetricFilterOption[]>>({});

    // Système de sélection de colonnes pour les tables
    const availableTableColumns = computed(() => {
      if (selectedMetricWidgetType.value !== StatsWidgetType.TABLE || !selectedMetric.value) {
        return [];
      }

      const tableWidget = selectedMetric.value.value[StatsWidgetType.TABLE];

      if (!tableWidget || !tableWidget.availableColumns) {
        return [];
      }

      // Exclure les colonnes avec isId: true (colonnes d'actions) qui doivent toujours être visibles
      return tableWidget.availableColumns.filter((col: any) => !col.isId);
    });

    /**
     * Groupes pour le MultiSelect Colonnes (table natif). Les colonnes
     * portent une `section` :
     *   - 'main'              → colonnes natives du widget
     *   - 'contact'           → fields contact additionnels (NativeTableExtensions)
     *   - 'custom:<idDef>'    → fields d'une data custom liée au contact
     * Le label de chaque section est résolu via i18n + sectionsMeta exposé
     * par le backend (cf. StatsService.enrichNativeTablesWithContactExtensions).
     */
    const tableColumnsSectionsMeta = computed(() => {
      const tableWidget = selectedMetric.value?.value?.[StatsWidgetType.TABLE];
      return (tableWidget as any)?.sectionsMeta ?? null;
    });

    const availableTableColumnsGroups = computed(() => {
      const cols = availableTableColumns.value as any[];
      if (cols.length === 0) return [];
      const groups: Array<{ label: string; items: any[] }> = [];

      const main = cols.filter((c) => !c.section || c.section === 'main');
      if (main.length > 0) {
        groups.push({
          label: t('statsEditor.widgets.widgetData.config.field.tableColumns.section.main'),
          items: main,
        });
      }

      const contact = cols.filter((c) => c.section === 'contact');
      if (contact.length > 0) {
        groups.push({
          label: t('statsEditor.widgets.widgetData.config.field.tableColumns.section.contact'),
          items: contact,
        });
      }

      // Une section par data custom liée au contact (ordre stable via sectionsMeta).
      const customDefs = (tableColumnsSectionsMeta.value?.customDataDefs ?? []) as Array<{
        idDefinition: number;
        name: string;
        sectionKey: string;
      }>;
      for (const def of customDefs) {
        const items = cols.filter((c) => c.section === def.sectionKey);
        if (items.length === 0) continue;
        groups.push({
          label: t(
            'statsEditor.widgets.widgetData.config.field.tableColumns.section.customData',
            { name: def.name },
          ),
          items,
        });
      }

      // Sections chaînées : data custom accessible via une junction
      // (contact → junction → target). Les valeurs sont concaténées via
      // GROUP_CONCAT côté SQL — header marqué "(liste)".
      const customChains = (tableColumnsSectionsMeta.value?.customDataChains ?? []) as Array<{
        junctionId: number;
        junctionName: string;
        targetId: number;
        targetName: string;
        sectionKey: string;
      }>;
      for (const chain of customChains) {
        const items = cols.filter((c) => c.section === chain.sectionKey);
        if (items.length === 0) continue;
        groups.push({
          label: t(
            'statsEditor.widgets.widgetData.config.field.tableColumns.section.customDataChain',
            { junction: chain.junctionName, target: chain.targetName },
          ),
          items,
        });
      }

      return groups;
    });

    const defaultTableColumns = computed<string[]>(() => {
      if (selectedMetricWidgetType.value !== StatsWidgetType.TABLE || !selectedMetric.value) {
        return [];
      }

      const tableWidget = selectedMetric.value.value[StatsWidgetType.TABLE];
      if (!tableWidget || !tableWidget.defaultColumns) {
        return [];
      }

      return tableWidget.defaultColumns;
    });

    // Colonnes obligatoires (ID/actions) qui doivent toujours être incluses
    const requiredTableColumns = computed<string[]>(() => {
      if (selectedMetricWidgetType.value !== StatsWidgetType.TABLE || !selectedMetric.value) {
        return [];
      }

      const tableWidget = selectedMetric.value.value[StatsWidgetType.TABLE];

      if (!tableWidget || !tableWidget.availableColumns) {
        return [];
      }

      // Récupérer les champs des colonnes avec isId: true
      return tableWidget.availableColumns
        .filter((col: any) => col.isId)
        .map((col: any) => col.field);
    });

    const selectedTableColumns = ref<string[]>([]);

    // Variables legacy pour compatibilité (seront supprimées progressivement)
    const listOfChannelFilters = computed<StatsPredefinedMetricFilterOption[]>(() => selectedMetricFilters.value?.channel ?? []);
    const selectedChannelFilters = ref<StatsPredefinedMetricFilterOption[]>([]);

    const listOfTypeFilters = computed<StatsPredefinedMetricFilterOption[]>(() => selectedMetricFilters.value?.type ?? []);
    const selectedTypeFilters = ref<StatsPredefinedMetricFilterOption[]>([]);

    const listOfBounceTypeFilters = computed<StatsPredefinedMetricFilterOption[]>(() => selectedMetricFilters.value?.bounce_type ?? []);
    const selectedBounceTypeFilters = ref<StatsPredefinedMetricFilterOption[]>([]);

    const hasMultipleSegmentSelections = computed(() => {
      if (!canIncludeTotal.value) {
        return false;
      }

      const dynamicSelections = Object.values(selectedFilters.value)
        .filter((selection): selection is StatsPredefinedMetricFilterOption[] => Array.isArray(selection));

      if (dynamicSelections.length) {
        return dynamicSelections.some((selection) => selection.length > 1);
      }

      const legacySelections = [
        selectedChannelFilters.value,
        selectedTypeFilters.value,
        selectedBounceTypeFilters.value,
      ];

      return legacySelections.some((selection) => selection.length > 1);
    });

    const showTotalOptions = computed(() => canIncludeTotal.value && hasMultipleSegmentSelections.value);

    const metricSearchTerm = ref('');
    const isMetricPickerOpen = ref(false);

    const openMetricPicker = () => {
      isMetricPickerOpen.value = true;
    };

    const closeMetricPicker = () => {
      isMetricPickerOpen.value = false;
    };

    const confirmMetricSelection = () => {
      if (!selectedMetric.value) {
        return;
      }
      closeMetricPicker();
    };

    // Fonctions helper pour le système de filtres généralisé
    const getFilterLabel = (filterKey: string): string => {
      const translationKey = `statsEditor.widgets.widgetData.config.field.filters.labels.${filterKey}`;
      const translated = t(translationKey);
      return translated !== translationKey ? translated : filterKey;
    };

    const getFilterPlaceholder = (filterKey: string): string => {
      const translationKey = `statsEditor.widgets.widgetData.config.field.filters.placeholders.${filterKey}`;
      const translated = t(translationKey);
      return translated !== translationKey ? translated : `Select ${filterKey}...`;
    };

    const getAvailableFiltersForChart = (
      categoryCode: string,
      chartType: string | null,
    ): Record<string, StatsPredefinedMetricFilterOption[]> => {
      const category = selectedChartCategory.value.find((cat) => cat.code === categoryCode);
      if (!category || !chartType || !category.charts[chartType]) {
        return {};
      }
      const filters = category.charts[chartType];
      // Convert Partial<Record> to Record by filtering out undefined values
      const result: Record<string, StatsPredefinedMetricFilterOption[]> = {};
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            result[key] = value;
          }
        });
      }
      return result;
    };

    const getAvailableFiltersForWidget = (): Record<string, StatsPredefinedMetricFilterOption[]> => {
      if (!selectedMetricFilters.value) {
        return {};
      }
      // Convert Partial<Record> to Record by filtering out undefined values
      // Exclure availableColumns et defaultColumns qui ne sont pas des filtres,
      // et `custom` qui est la sentinelle de config Custom KPI (objet, pas un array d'options).
      const result: Record<string, StatsPredefinedMetricFilterOption[]> = {};
      Object.entries(selectedMetricFilters.value).forEach(([key, value]) => {
        if (
          value !== undefined
          && Array.isArray(value)
          && key !== 'availableColumns'
          && key !== 'defaultColumns'
          && key !== 'custom'
        ) {
          result[key] = value;
        }
      });
      return result;
    };

    const normalizeSavedFilterValues = (values: string[]): string[] => values
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    const matchFilterOptionWithCode = (
      options: StatsPredefinedMetricFilterOption[] | undefined,
      code: string,
    ): StatsPredefinedMetricFilterOption | undefined => {
      if (!options || !options.length) {
        return undefined;
      }

      const normalizedCode = code.trim();
      if (!normalizedCode) {
        return undefined;
      }

      const normalizedCodeLower = normalizedCode.toLowerCase();

      const matched = options.find((option) => {
        if (!option) {
          return false;
        }

        const optionCode = option.code?.trim();
        if (optionCode && optionCode.toLowerCase() === normalizedCodeLower) {
          return true;
        }

        const rawValues = option.rawValues ?? [];
        if (rawValues.some((value) => value.trim().toLowerCase() === normalizedCodeLower)) {
          return true;
        }

        if (
          option.legacyValue &&
          option.legacyValue.trim().toLowerCase() === normalizedCodeLower
        ) {
          return true;
        }

        if (
          option.normalizedValue &&
          option.normalizedValue.trim().toLowerCase() === normalizedCodeLower
        ) {
          return true;
        }

        return false;
      });

      if (!matched) {
        return undefined;
      }

      return { ...matched };
    };

    const restoreFilterSelections = (
      options: StatsPredefinedMetricFilterOption[] | undefined,
      savedValues: string[],
    ): StatsPredefinedMetricFilterOption[] => {
      if (!options || !options.length || !savedValues.length) {
        return [];
      }

      const normalizedSavedValues = normalizeSavedFilterValues(savedValues);
      if (!normalizedSavedValues.length) {
        return [];
      }

      const selections: StatsPredefinedMetricFilterOption[] = [];

      normalizedSavedValues.forEach((savedValue) => {
        const matched = matchFilterOptionWithCode(options, savedValue);
        if (!matched) {
          return;
        }

        if (
          !selections.some(
            (selection) =>
              selection.code?.trim().toLowerCase() === matched.code?.trim().toLowerCase(),
          )
        ) {
          selections.push(matched);
        }
      });

      return selections;
    };

    const cloneFilterOptions = (
      options: StatsPredefinedMetricFilterOption[],
    ): StatsPredefinedMetricFilterOption[] => options.map((option) => ({
      ...option,
      rawValues: option.rawValues ? [...option.rawValues] : undefined,
    }));

    watch(isMetricPickerOpen, (visible) => {
      if (!visible) {
        metricSearchTerm.value = '';
        metricTypeFilters.value = new Set();
      }
    });
    const metricGroups = computed<StatsPredefinedMetricGroup[]>(() => (Array.isArray(props.predefinedMetrics)
      ? props.predefinedMetrics
      : []));

    /**
     * Masquage temporaire de la section "Mes KPIs personnalisés" du sélecteur
     * de statistiques (feature pas assez testée). Les "Tableaux de données
     * personnalisées" (marqueur `rawCustomData`) restent visibles.
     * En filtrant à la source, le compteur de l'onglet "Personnalisé" et la
     * recherche globale restent cohérents (ils n'exposent plus les KPIs custom).
     * Restaurer : passer cette constante à `false`.
     */
    const HIDE_CUSTOM_KPI_SECTION = true;

    /**
     * Helper : détecte si une metric custom est un Tableau brut auto-généré
     * (présence du marqueur `rawCustomData` sur n'importe quel widgetType
     * exposé). Utilisé pour scinder l'onglet Personnalisé en deux sections
     * (KPIs custom vs Tables brutes) et pour le masquage ci-dessus.
     */
    const hasRawCustomDataMarker = (metric: any): boolean => {
      const types = metric?.value ?? {};
      return Object.values(types).some((v: any) => v?.rawCustomData);
    };

    // Widgets réservés aux boutiques multi-sources : même règle que le header.
    // Seul le catalogue les masque ; un widget déjà posé se rouvre normalement.
    const isMultiSource = ref(false);
    fetchIsMultiSourceShop().then((multiSource) => {
      isMultiSource.value = multiSource;
    });

    const visibleMetricGroups = computed<StatsPredefinedMetricGroup[]>(() => metricGroups.value.reduce<StatsPredefinedMetricGroup[]>((acc, group) => {
      const visibleItems = group.items.reduce<StatsPredefinedMetricCategory[]>((categoryAcc, category) => {
        const visibleMetrics = category.value.filter((metric) => {
          if (metric.legacy) return false;
          if (!isMetricAvailableForShop(metric, isMultiSource.value)) return false;
          // KPIs personnalisés masqués : dans le groupe "custom", on ne
          // conserve que les Tableaux de données (marqueur rawCustomData).
          if (HIDE_CUSTOM_KPI_SECTION && group.key === 'custom' && !hasRawCustomDataMarker(metric)) {
            return false;
          }
          return true;
        });
        if (visibleMetrics.length) {
          categoryAcc.push({
            ...category,
            value: visibleMetrics,
          });
        }
        return categoryAcc;
      }, []);

      if (visibleItems.length) {
        acc.push({
          ...group,
          items: visibleItems,
        });
      }

      return acc;
    }, []));
    // Number of metrics per group (sum of metrics across all visible categories)
    const groupMetricsCount = computed<Record<string, number>>(() => {
      const map: Record<string, number> = {};
      visibleMetricGroups.value.forEach((group) => {
        const total = group.items.reduce((sum, category) => sum + (category.value?.length || 0), 0);
        map[group.key] = total;
      });
      return map;
    });
    const activeGroupKey: Ref<string | null> = ref(null);
    const hydratedFromConfiguration = ref(false);

    // Watch sur widgetConfiguration pour détecter les vraies modifications externes
    // (quand le composant est recréé avec une nouvelle configuration)
    watch(() => props.modelValue.widgetConfiguration, (newConfig) => {
      // Synchroniser widgetConfiguration locale avec les nouvelles props
      widgetConfiguration.value = newConfig;
    }, { deep: true, immediate: false });

    const isMetricSelected = (metric: StatsPredefinedMetricOption) => !!selectedMetric.value
      && selectedMetric.value.group === metric.group
      && selectedMetric.value.category === metric.category
      && selectedMetric.value.metric === metric.metric;

    const getMetricAvailableTypes = (metric: StatsPredefinedMetricOption) => Object.keys(metric.value ?? {});

    const getMetricTypeLabel = (type: string) => t(`statsEditor.widgets.widgetData.config.field.widgetType.types.${type}.label`);
    const getMetricTypeIcon = (type: string) => t(`statsEditor.widgets.widgetData.config.field.widgetType.types.${type}.icon`);

    const selectMetricOption = (metric: StatsPredefinedMetricOption, groupKey?: string) => {
      if (!metric) {
        return;
      }

      const previousMetricKey = selectedMetric.value
        ? `${selectedMetric.value.group}.${selectedMetric.value.category}.${selectedMetric.value.metric}`
        : null;
      const nextMetricKey = `${metric.group}.${metric.category}.${metric.metric}`;

      selectedMetric.value = metric;
      const resolvedGroupKey = groupKey ?? metric.group;
      if (resolvedGroupKey) {
        activeGroupKey.value = resolvedGroupKey;
      }

      const configurationMetricKey = widgetConfiguration.value
        ? `${widgetConfiguration.value.group}.${widgetConfiguration.value.category}.${widgetConfiguration.value.metric}`
        : null;

      const isManualChange = previousMetricKey !== nextMetricKey && configurationMetricKey !== nextMetricKey;

      // Si on change manuellement de métrique (pas une restauration), reset le flag
      if (isManualChange) {
        includeTotal.value = false;
        totalOnly.value = false;
        // Reset le flag car c'est un changement manuel, pas une restauration
        hydratedFromConfiguration.value = false;
      }

      // Ne marquer comme "hydrated" que si c'est vraiment une restauration (avec charts ou filtres)
      // ET seulement si ce n'est PAS un changement manuel de métrique
      if (!hydratedFromConfiguration.value && widgetConfiguration.value && !isManualChange) {
        const hasChartsConfig = widgetConfiguration.value.charts && widgetConfiguration.value.charts.length > 0;
        const hasFiltersConfig = widgetConfiguration.value.filters && Object.keys(widgetConfiguration.value.filters).length > 0;
        if (hasChartsConfig || hasFiltersConfig) {
          hydratedFromConfiguration.value = true;
        }
      }

      metricSearchTerm.value = '';
    };

    const selectGroup = (groupKey: string) => {
      activeGroupKey.value = groupKey;
    };

    const isSearching = computed(() => metricSearchTerm.value.trim().length > 0);

    const flattenedMetrics = computed<FlattenedMetricEntry[]>(() => visibleMetricGroups.value.reduce<FlattenedMetricEntry[]>((acc, group) => {
      if (group.items && group.items.length) {
        group.items.forEach((category) => {
          if (category.value && category.value.length) {
            category.value.forEach((metric) => {
              acc.push({
                groupKey: group.key,
                groupLabel: group.label,
                categoryKey: category.key,
                categoryLabel: category.label,
                data: metric,
              });
            });
          }
        });
      }

      return acc;
    }, []));

    const searchResults = computed<FlattenedMetricEntry[]>(() => {
      if (!isSearching.value) {
        return [];
      }

      const term = metricSearchTerm.value.trim().toLowerCase();
      return flattenedMetrics.value.filter((entry) => {
        if (!matchesMetricTypeFilters(entry.data)) return false;
        const metricLabel = (entry.data.childrenLabel || '').toLowerCase();
        const description = ((entry.data as { description?: string | null }).description || '').toLowerCase();
        const groupLabel = (entry.groupLabel || '').toLowerCase();
        const categoryLabel = (entry.categoryLabel || '').toLowerCase();

        return metricLabel.includes(term)
          || description.includes(term)
          || groupLabel.includes(term)
          || categoryLabel.includes(term);
      });
    });

    const activeGroup = computed<StatsPredefinedMetricGroup | undefined>(() => metricGroups.value
      .find((group) => group.key === activeGroupKey.value));
    const visibleActiveGroup = computed<StatsPredefinedMetricGroup | undefined>(() => visibleMetricGroups.value
      .find((group) => group.key === activeGroupKey.value));
    const activeGroupCategories = computed<StatsPredefinedMetricCategory[]>(() => (visibleActiveGroup.value
      ? visibleActiveGroup.value.items
      : []));

    /**
     * Same as `activeGroupCategories` mais applique le filtre type d'affichage
     * (chips KPI/Tableau/Graphique). Une catégorie qui se retrouverait sans
     * metric après filtrage est masquée pour éviter les "trous" visuels.
     */
    const filteredActiveGroupCategories = computed<StatsPredefinedMetricCategory[]>(() => activeGroupCategories.value
      .map((category) => ({
        ...category,
        value: (category.value ?? []).filter(matchesMetricTypeFilters),
      }))
      .filter((category) => category.value.length > 0));

    // ===== Filtre par type d'affichage (chips KPI/Tableau/Graphique) =====
    const availableMetricTypeFilters = [
      StatsWidgetType.KPI,
      StatsWidgetType.TABLE,
      StatsWidgetType.CHART,
    ] as const;
    const metricTypeFilters = ref<Set<StatsWidgetType>>(new Set());

    function isMetricTypeFilterActive(type: StatsWidgetType) {
      return metricTypeFilters.value.has(type);
    }
    function toggleMetricTypeFilter(type: StatsWidgetType) {
      const next = new Set(metricTypeFilters.value);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      metricTypeFilters.value = next;
    }
    function resetMetricTypeFilters() {
      metricTypeFilters.value = new Set();
    }
    /** True si la metric expose au moins un des types actuellement filtrés. */
    const matchesMetricTypeFilters = (metric: StatsPredefinedMetricOption) => {
      if (metricTypeFilters.value.size === 0) return true;
      const exposed = Object.keys(metric.value ?? {}) as StatsWidgetType[];
      return exposed.some((t) => metricTypeFilters.value.has(t));
    };

    /**
     * Vue plate des metrics du groupe `custom` : on aplatit catégorie ×
     * metric en une seule liste car chaque catégorie custom ne contient
     * qu'une metric (le titre serait redondant avec celui de la card).
     * Filtré par les chips si actifs.
     */
    const flatCustomMetrics = computed(() => activeGroupCategories.value.flatMap((category) =>
      (category.value ?? [])
        .filter(matchesMetricTypeFilters)
        .map((metric) => ({
          categoryKey: category.key,
          groupKey: category.groupKey ?? activeGroupKey.value ?? '',
          metric,
        })),
    ));

    /** KPIs custom (créés via l'éditeur) — exclut les Tables brutes. */
    const kpiCustomEntries = computed(() => flatCustomMetrics.value.filter(
      (entry) => !hasRawCustomDataMarker(entry.metric),
    ));

    /** Tableaux bruts auto-générés sur les data custom definitions. */
    const rawCustomEntries = computed(() => flatCustomMetrics.value.filter(
      (entry) => hasRawCustomDataMarker(entry.metric),
    ));

    /**
     * Description courte affichée sur la card. Préfère la clé i18n
     * `statsEditor.widgets.widgetData.config.field.metric.descriptions.<group>.<category>.<metric>`
     * (locale-aware : FR ou EN selon la langue de l'interface). Fallback
     * sur la `description` portée par le metric (les KPI custom héritent
     * de la description saisie à la création, ou natives qui exposent
     * un texte hardcodé). Si rien → null (la card masque le bloc).
     */
    const getMetricDescription = (metric: StatsPredefinedMetricOption): string | null => {
      const key = `statsEditor.widgets.widgetData.config.field.metric.descriptions.${metric.group}.${metric.category}.${metric.metric}`;
      const translated = t(key);
      if (translated && translated !== key) return translated;
      const inline = (metric as { description?: string | null }).description;
      return inline || null;
    };
    const selectedMetricContext = computed(() => {
      const metric = selectedMetric.value;
      if (!metric) {
        return null;
      }

      const group = metricGroups.value.find((item) => item.key === metric.group);
      const category = group?.items.find((item) => item.key === metric.category);

      if (!group && !category) {
        return null;
      }

      return {
        groupLabel: group?.label ?? '',
        categoryLabel: category?.label ?? '',
      };
    });

    watch([selectedChartCategory], ([newCategories], [previousCategories]) => {
      // Ne pas modifier les sélections pendant la restauration
      if (hydratedFromConfiguration.value) {
        return;
      }

      // Si la catégorie sélectionnée est 'all' → on retire toutes les autres catégories
      const lastElement = selectedChartCategory.value.length
        ? selectedChartCategory.value[selectedChartCategory.value.length - 1]
        : null;
      if (lastElement && (newCategories.length !== (previousCategories ? previousCategories.length : 0))) {
        selectedChartCategory.value = (lastElement.code === 'all')
          ? [lastElement]
          : selectedChartCategory.value.filter((item) => item.code !== 'all');
      }

      if (isRepartitionMode.value && selectedChartCategory.value.length > 1) {
        const lastSelected = selectedChartCategory.value[selectedChartCategory.value.length - 1];
        selectedChartCategory.value = lastSelected ? [lastSelected] : [];
      }

      const selectedCategoryCodes = selectedChartCategory.value.map((item) => item.code);

      // Sauvegarder les filtres des catégories supprimées pour les transférer si possible
      const previousFilters: Record<string, any> = {};
      Object.keys(selectedChartTypeByCat.value).forEach((categoryCode) => {
        if (!selectedCategoryCodes.includes(categoryCode)) {
          // Sauvegarder les filtres avant de supprimer la catégorie
          if (selectedFiltersByCat.value[categoryCode]) {
            Object.assign(previousFilters, selectedFiltersByCat.value[categoryCode]);
          }
          delete selectedChartTypeByCat.value[categoryCode];
        }
      });

      selectedCategoryCodes.forEach((categoryCode) => {
        if (!selectedChartTypeByCat.value[categoryCode]) {
          // En mode répartition, sélectionner automatiquement le type global
          if (selectedChartDisplayMode.value === 'repartition' && repartitionChartType.value) {
            selectedChartTypeByCat.value[categoryCode] = repartitionChartType.value;
          } else {
            selectedChartTypeByCat.value[categoryCode] = '';
          }
        }
        if (!selectedFiltersByCat.value[categoryCode]) {
          // Auto-sélectionner tous les filtres disponibles pour cette catégorie
          const category = selectedChartCategory.value.find(c => c.code === categoryCode);
          if (category) {
            const chartType = selectedChartTypeByCat.value[categoryCode];
            const availableFilters = getAvailableFiltersForChart(categoryCode, chartType);

            selectedFiltersByCat.value[categoryCode] = {};
            Object.entries(availableFilters).forEach(([filterKey, filterOptions]) => {
              if (filterOptions && filterOptions.length > 0) {
                // Essayer de transférer les filtres précédents si le même filterKey existe
                if (previousFilters[filterKey] && Array.isArray(previousFilters[filterKey]) && previousFilters[filterKey].length > 0) {
                  // Filtrer pour ne garder que les options valides dans la nouvelle catégorie
                  const validPreviousFilters = previousFilters[filterKey].filter((prevFilter: any) =>
                    filterOptions.some((opt: any) => opt.code === prevFilter.code)
                  );

                  if (validPreviousFilters.length > 0) {
                    selectedFiltersByCat.value[categoryCode][filterKey] = validPreviousFilters;
                  } else {
                    // Si aucun filtre précédent n'est valide, auto-sélectionner tous
                    selectedFiltersByCat.value[categoryCode][filterKey] = [...filterOptions];
                  }
                } else {
                  // Pas de filtres précédents, auto-sélectionner tous
                  selectedFiltersByCat.value[categoryCode][filterKey] = [...filterOptions];
                }
              }
            });
          } else {
            selectedFiltersByCat.value[categoryCode] = {};
          }
        }
      });

      if (selectedChartDisplayMode.value === 'timeline') {
        if (customizeChartTypePerCategory.value) {
          selectedChartCategory.value.forEach((category) => {
            const availableTypes = getTimelineChartTypesForCategory(category);
            if (availableTypes.length === 1) {
              [selectedChartTypeByCat.value[category.code]] = availableTypes;
            } else if (selectedChartTypeByCat.value[category.code]
              && !availableTypes.includes(selectedChartTypeByCat.value[category.code] as ChartType)) {
              selectedChartTypeByCat.value[category.code] = '';
            }
          });
        } else {
          synchronizeGlobalChartType();
        }
      } else if (selectedChartDisplayMode.value === 'repartition') {
        const availableTypes = repartitionChartTypeOptions.value.map(({ key }: ChartTypeOption) => key);
        if (!availableTypes.length) {
          repartitionChartType.value = '';
          selectedCategoryCodes.forEach((categoryCode) => {
            selectedChartTypeByCat.value[categoryCode] = '';
          });
        } else if (!repartitionChartType.value || !availableTypes.includes(repartitionChartType.value as ChartType)) {
          [repartitionChartType.value] = availableTypes;
          selectedCategoryCodes.forEach((categoryCode) => {
            selectedChartTypeByCat.value[categoryCode] = repartitionChartType.value;
          });
        } else {
          selectedCategoryCodes.forEach((categoryCode) => {
            selectedChartTypeByCat.value[categoryCode] = repartitionChartType.value;
          });
        }
      }

      Object.keys(selectedChannelFiltersByCat.value).forEach((categoryCode) => {
        if (!selectedCategoryCodes.includes(categoryCode)) {
          selectedChannelFiltersByCat.value[categoryCode] = [];
        }
      });
      Object.keys(selectedTypeFiltersByCat.value).forEach((categoryCode) => {
        if (!selectedCategoryCodes.includes(categoryCode)) {
          selectedTypeFiltersByCat.value[categoryCode] = [];
        }
      });
      Object.keys(selectedBounceTypeFiltersByCat.value).forEach((categoryCode) => {
        if (!selectedCategoryCodes.includes(categoryCode)) {
          selectedBounceTypeFiltersByCat.value[categoryCode] = [];
        }
      });

      Object.entries(selectedFiltersByCat.value).forEach(([categoryCode, filters]) => {
        if (!selectedCategoryCodes.includes(categoryCode)) {
          delete selectedFiltersByCat.value[categoryCode];
          return;
        }

        const selectedChartType = selectedChartTypeByCat.value[categoryCode];
        const availableFilters = getAvailableFiltersForChart(categoryCode, selectedChartType ?? null);
        Object.keys(filters).forEach((filterKey) => {
          if (!availableFilters[filterKey]) {
            delete filters[filterKey];
          }
        });
      });

      if (isTimelineGlobalMode.value) {
        syncGlobalTimelineFiltersFromCategories();
        applyGlobalTimelineFiltersToCategories();
      }
    });

    watch(customizeChartTypePerCategory, (enabled) => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        return;
      }

      if (enabled) {
        selectedChartCategory.value.forEach((category) => {
          if (!selectedChartTypeByCat.value[category.code]) {
            const availableTypes = getTimelineChartTypesForCategory(category);
            if (availableTypes.length) {
              [selectedChartTypeByCat.value[category.code]] = availableTypes;
            }
          }
        });
      } else {
        synchronizeGlobalChartType();
        syncGlobalTimelineFiltersFromCategories();
        applyGlobalTimelineFiltersToCategories();
      }
    });

    watch(globalTimelineChartTypeOptions, (options) => {
      if (selectedChartDisplayMode.value !== 'timeline') {
        return;
      }

      if (!options.length && !customizeChartTypePerCategory.value) {
        customizeChartTypePerCategory.value = true;
        return;
      }

      if (!customizeChartTypePerCategory.value) {
        ensureGlobalChartTypeIsValid();
        applyGlobalChartTypeToCategories();
        syncGlobalTimelineFiltersFromCategories();
        applyGlobalTimelineFiltersToCategories();
      }
    }, { deep: true });

    watch(globalChartType, () => {
      if (selectedChartDisplayMode.value !== 'timeline' || customizeChartTypePerCategory.value) {
        return;
      }

      applyGlobalChartTypeToCategories();
      syncGlobalTimelineFiltersFromCategories();
      applyGlobalTimelineFiltersToCategories();
    });

    watch(globalTimelineFilterOptions, () => {
      if (!isTimelineGlobalMode.value) {
        if (Object.keys(globalTimelineFiltersSelection.value).length) {
          globalTimelineFiltersSelection.value = {};
        }
        return;
      }

      syncGlobalTimelineFiltersFromCategories();
      applyGlobalTimelineFiltersToCategories();
    }, { deep: true });

    watch(globalTimelineFiltersSelection, () => {
      if (!isTimelineGlobalMode.value) {
        return;
      }

      applyGlobalTimelineFiltersToCategories();
    }, { deep: true });

    watch(selectedFiltersByCat, () => {
      if (!isTimelineGlobalMode.value) {
        return;
      }

      syncGlobalTimelineFiltersFromCategories();
    }, { deep: true });

    watch(isTimelineGlobalMode, (isGlobalMode) => {
      if (!isGlobalMode) {
        if (Object.keys(globalTimelineFiltersSelection.value).length) {
          globalTimelineFiltersSelection.value = {};
        }
        return;
      }

      syncGlobalTimelineFiltersFromCategories();
      applyGlobalTimelineFiltersToCategories();
    });

    const ensureRepartitionSelection = () => {
      if (!isRepartitionMode.value) {
        return;
      }

      const dimensions = repartitionDimensionOptions.value;

      if (!dimensions.length) {
        selectedRepartitionDimensionKey.value = null;
        selectedRepartitionMetricKey.value = null;
        selectedChartCategory.value = [];
        return;
      }

      if (!dimensions.some((dimension) => dimension.code === selectedRepartitionDimensionKey.value)) {
        // Vérifier si les dimensions sont des groupes ou des catégories directes
        const availableCategoryCodes = new Set(
          listOfChartCategories.value
            .filter((cat) => hasModeForCategory(cat, 'repartition'))
            .map((cat) => cat.code)
        );

        // Si la première dimension est une catégorie directe (pas un groupe), la sélectionner
        const firstDimIsCategory = availableCategoryCodes.has(dimensions[0]?.code);

        if (firstDimIsCategory) {
          selectedRepartitionDimensionKey.value = dimensions[0].code;
        } else {
          // Sinon c'est un groupe, chercher celui qui a des variantes compatibles
          const compatibleDimension = dimensions.find((dim) => {
            const variants = chartCategoryGroups.value[dim.code]?.variants ?? [];
            return variants.some((variant) => availableCategoryCodes.has(variant));
          });

          selectedRepartitionDimensionKey.value = compatibleDimension?.code ?? dimensions[0].code;
        }
      }

      if (!shouldUseRepartitionMetricSelector.value) {
        applyRepartitionSelection();
        return;
      }

      const metrics = repartitionMetricOptions.value;

      if (!metrics.length) {
        selectedRepartitionMetricKey.value = null;
        selectedChartCategory.value = [];
        return;
      }

      if (!metrics.some((metric) => metric.code === selectedRepartitionMetricKey.value)) {
        selectedRepartitionMetricKey.value = metrics[0].code;
      }

      applyRepartitionSelection();
    };

    watch(isRepartitionMode, (isMode) => {
      if (!isMode) {
        selectedRepartitionDimensionKey.value = null;
        selectedRepartitionMetricKey.value = null;
        return;
      }

      ensureRepartitionSelection();
    });

    watch(repartitionDimensionOptions, ensureRepartitionSelection, { deep: true });

    watch(repartitionMetricOptions, ensureRepartitionSelection, { deep: true });

    watch(selectedRepartitionDimensionKey, () => {
      if (!isRepartitionMode.value) {
        return;
      }

      // Ne pas appliquer la sélection automatique pendant la restauration
      if (hydratedFromConfiguration.value) {
        return;
      }

      if (!shouldUseRepartitionMetricSelector.value) {
        applyRepartitionSelection();
        return;
      }

      const metrics = repartitionMetricOptions.value;

      if (!metrics.length) {
        selectedRepartitionMetricKey.value = null;
        selectedChartCategory.value = [];
        return;
      }

      if (!metrics.some((metric) => metric.code === selectedRepartitionMetricKey.value)) {
        selectedRepartitionMetricKey.value = metrics[0].code;
        return;
      }

      applyRepartitionSelection();
    });

    watch(selectedRepartitionMetricKey, () => {
      if (!isRepartitionMode.value) {
        return;
      }

      // Ne pas appliquer la sélection automatique pendant la restauration
      if (hydratedFromConfiguration.value) {
        return;
      }

      if (!shouldUseRepartitionMetricSelector.value) {
        return;
      }

      applyRepartitionSelection();
    });

    watch(availableChartCategories, () => {
      if (isRepartitionMode.value && !hydratedFromConfiguration.value) {
        applyRepartitionSelection();
      }
    }, { deep: true });

    watch(listOfChartCategories, (categories) => {
      if (!categories.length) {
        if (selectedChartDisplayMode.value !== null) {
          clearChartSelections();
        }
        selectedChartDisplayMode.value = null;
        return;
      }

      const availableModes = chartDisplayModeOptions.value.filter((mode) => !mode.disabled);
      if (!availableModes.length) {
        if (selectedChartDisplayMode.value !== null) {
          clearChartSelections();
        }
        selectedChartDisplayMode.value = null;
        return;
      }

      if (selectedChartDisplayMode.value
        && availableModes.some((mode) => mode.key === selectedChartDisplayMode.value)) {
        return;
      }

      clearChartSelections();
      selectedChartDisplayMode.value = availableModes[0].key;
    }, { immediate: true, deep: true });

    watch(repartitionChartType, (newType) => {
      if (selectedChartDisplayMode.value !== 'repartition') {
        return;
      }

      selectedChartCategory.value.forEach((category) => {
        selectedChartTypeByCat.value[category.code] = newType || '';
      });
    });

    const getChartCategoryLabel = (
      category: string,
      metric?: StatsPredefinedMetricOption,
    ) => {
      const domainKey = metric?.metric?.includes('orders') || metric?.group?.includes('orders')
        ? 'orders'
        : metric?.metric?.includes('carts') || metric?.group?.includes('carts')
          ? 'carts'
          : null;

      const domainLabelKeys = domainKey
        ? [
          `statsEditor.widgets.widgetData.config.charts.categories.${domainKey}.lists.${category}`,
          `statsEditor.widgets.widgetData.config.charts.${domainKey}.lists.${category}`,
        ]
        : [];

      const genericLabelKey = `statsEditor.widgets.widgetData.config.charts.categories.lists.${category}`;

      for (const labelKey of domainLabelKeys) {
        if (te(labelKey)) {
          return t(labelKey);
        }
      }

      if (te(genericLabelKey)) {
        return t(genericLabelKey);
      }

      return category;
    };

    const selectMetricWidgetType = (type: StatsWidgetType | null) => {
      if (selectedMetricWidgetType.value === type) return;
      selectedMetricWidgetType.value = type;

      if (type !== StatsWidgetType.KPI) {
        includeTotal.value = false;
        totalOnly.value = false;
      }

      // init chart data
      clearChartSelections();
      listOfChartCategories.value = [];
      listOfChartCategoriesIsMultiple.value = false;
      selectedChartDisplayMode.value = null;

      // init KPI and TABLE data
      selectedChannelFilters.value = [];
      selectedTypeFilters.value = [];
      selectedBounceTypeFilters.value = [];

      if (!type) {
        return;
      }

      const metric = selectedMetric.value;
      if (!metric) {
        return;
      }

      chartCategoryGroups.value = {};
      /*
        Si c'est un widget de type CHART
        → on récupère la liste des catégories du "chart" ainsi que les types de chart et filtres associés à chaque catégorie
       */
      if (type === StatsWidgetType.CHART) {
        listOfChartCategoriesIsMultiple.value = metric.allowMultiple;
        const chartValues = metric.value[StatsWidgetType.CHART] ?? {};
        // Sépare `groups` ET `__variantsMeta` (v3 games) des vraies catégories.
        // Sinon ces clés "magiques" seraient itérées comme des variants.
        const {
          groups: groupsConfig,
          __variantsMeta: variantsMeta,
          ...categoryValues
        } = chartValues as Record<string, any>;
        chartCategoryGroups.value = groupsConfig && typeof groupsConfig === 'object' ? groupsConfig : {};
        const variantsMetaMap: Record<string, RepartitionVariantMeta> =
          variantsMeta && typeof variantsMeta === 'object' ? variantsMeta : {};
        const shouldHideLegacyGlobal = metricsWithLegacyGlobalCategory.includes(metric.metric);
        const hasChannelsCategory = Object.prototype.hasOwnProperty.call(categoryValues, 'channels');

        listOfChartCategories.value = Object.keys(categoryValues).reduce<ChartCategoryOption[]>((acc, category) => {
          if (category === 'chartOptions') {
            return acc;
          }

          if (shouldHideLegacyGlobal && hasChannelsCategory && category === 'all') {
            return acc;
          }

          const chartsTypeByCat = JSON.parse(
            JSON.stringify(categoryValues[category]),
          ) as Record<string, StatsPredefinedMetricFilters>;

          // init selected chart type by category
          selectedChartTypeByCat.value[category] = '';

          // Ne pas réinitialiser selectedFiltersByCat si on est en train de restaurer une configuration
          if (!hydratedFromConfiguration.value) {
            selectedFiltersByCat.value[category] = {};
          }

          // init selected channels by category
          selectedChannelFiltersByCat.value[category] = [];
          selectedTypeFiltersByCat.value[category] = [];
          selectedBounceTypeFiltersByCat.value[category] = [];

          const meta = variantsMetaMap[category];
          acc.push({
            name: getChartCategoryLabel(category, metric),
            code: category,
            charts: chartsTypeByCat,
            ...(meta && (meta.dimension || meta.measure)
              ? { charts_meta: meta }
              : {}),
            hidden: false,
          });

          return acc;
        }, []);

        // Fallback grouping for traffic identity metrics to expose both dimension and value selectors
        if (
          metric.metric === 'traffic-identity'
          && !Object.keys(chartCategoryGroups.value).length
        ) {
          const inferredGroups: Record<string, { label?: string; variants: string[] }> = {};
          const availableVariants = Object.keys(categoryValues);
          ['loginStatus', 'contactStatus'].forEach((groupKey) => {
            const variants = availableVariants.filter((category) => category.startsWith(groupKey));
            if (variants.length) {
              inferredGroups[groupKey] = {
                label: getChartCategoryLabel(groupKey, metric),
                variants,
              };
            }
          });

          if (Object.keys(inferredGroups).length) {
            chartCategoryGroups.value = inferredGroups;
          }
        }

        return;
      }

      // Auto-select all available filters for KPI and TABLE widgets
      // Utiliser le nouveau système de filtres dynamiques (selectedFilters)
      const availableFilters = getAvailableFiltersForWidget();

      Object.keys(availableFilters).forEach((filterKey) => {
        const filterOptions = availableFilters[filterKey];
        if (filterOptions && filterOptions.length > 0) {
          selectedFilters.value[filterKey] = [...filterOptions];
        }
      });

      // Maintenir aussi l'ancien système legacy pour compatibilité
      if (listOfChannelFilters.value && listOfChannelFilters.value.length > 0) {
        selectedChannelFilters.value = [...listOfChannelFilters.value];
      }

      if (listOfTypeFilters.value && listOfTypeFilters.value.length > 0) {
        selectedTypeFilters.value = [...listOfTypeFilters.value];
      }

      if (listOfBounceTypeFilters.value && listOfBounceTypeFilters.value.length > 0) {
        selectedBounceTypeFilters.value = [...listOfBounceTypeFilters.value];
      }
    };

    // select chart by category for CHART
    const selectChartTypeByCategory = (category: string, type: string) => {
      selectedChartTypeByCat.value[category] = type;

      // Initialiser selectedFiltersByCat pour cette catégorie si elle n'existe pas
      if (!selectedFiltersByCat.value[category]) {
        selectedFiltersByCat.value[category] = {};
      }
    };

    // Select all first elements in configuration panel for a widget
    function setDefaultConfigurationForWidget() {
      const metric = selectedMetric.value;
      if (!metric) {
        return;
      }

      if (!selectedChartDisplayMode.value) {
        return;
      }

      // Ne pas modifier les sélections si la configuration a déjà été restaurée
      if (hydratedFromConfiguration.value) {
        return;
      }

      const availableCategories = availableChartCategories.value;

      if (!selectedChartCategory.value.length && availableCategories.length) {
        if (selectedChartDisplayMode.value === 'repartition') {
          ensureRepartitionSelection();
        } else {
          selectedChartCategory.value = metric.allowMultiple
            ? availableCategories.reduce<ChartCategoryOption[]>((acc, category) => {
              if ((availableCategories.length > 0 && category.code !== 'all')
                || (availableCategories.length === 1 && category.code === 'all')) {
                // Exclude "all" except if this is the only option
                acc.push(category);
              }

              return acc;
            }, [])
            : [availableCategories[0]];
        }
      }

      if (selectedChartDisplayMode.value === 'timeline') {
        if (customizeChartTypePerCategory.value) {
          selectedChartCategory.value.forEach((category) => {
            const chartTypes = getTimelineChartTypesForCategory(category);
            if (chartTypes.length) {
              if (chartTypes.includes(selectedChartTypeByCat.value[category.code] as ChartType)) {
                return;
              }
              [selectedChartTypeByCat.value[category.code]] = chartTypes;
            }
          });
        } else {
          synchronizeGlobalChartType();
        }
      } else if (selectedChartDisplayMode.value === 'repartition') {
        const availableTypes = repartitionChartTypeOptions.value.map(({ key }: ChartTypeOption) => key);
        if (!repartitionChartType.value && availableTypes.length) {
          repartitionChartType.value = availableTypes[0];
        }

        // Appliquer le type de graphique de répartition à toutes les catégories
        if (repartitionChartType.value && availableTypes.includes(repartitionChartType.value as ChartType)) {
          selectedChartCategory.value.forEach((category) => {
            selectedChartTypeByCat.value[category.code] = repartitionChartType.value;
          });
        }
      }

      selectedChartCategory.value.forEach((category) => {
        const resolvedChartType = selectedChartTypeByCat.value[category.code] ?? null;
        if (!resolvedChartType) {
          return;
        }

        const chartFilters = category.charts[resolvedChartType];
        if (!chartFilters) {
          return;
        }

        if (!selectedFiltersByCat.value[category.code]) {
          selectedFiltersByCat.value[category.code] = {};
        }

        (Object.entries(chartFilters) as [string, StatsPredefinedMetricFilterOption[] | undefined][])
          .forEach(([filterKey, filterOptions]) => {
            if (!Array.isArray(filterOptions) || !filterOptions.length) {
              return;
            }

            const currentSelection = selectedFiltersByCat.value[category.code][filterKey] ?? [];
            if (currentSelection.length) {
              return;
            }

            selectedFiltersByCat.value[category.code][filterKey] = cloneFilterOptions(filterOptions);
          });
      });

      if (!selectedChannelFilters.value.length && listOfChannelFilters.value.length) {
        selectedChannelFilters.value = listOfChannelFilters.value.reduce<StatsPredefinedMetricFilterOption[]>((acc, filter) => {
          acc.push(filter);
          return acc;
        }, []);
      }

      if (!selectedTypeFilters.value.length && listOfTypeFilters.value.length) {
        selectedTypeFilters.value = listOfTypeFilters.value.reduce<StatsPredefinedMetricFilterOption[]>((acc, filter) => {
          acc.push(filter);
          return acc;
        }, []);
      }

      if (!selectedBounceTypeFilters.value.length && listOfBounceTypeFilters.value.length) {
        selectedBounceTypeFilters.value = listOfBounceTypeFilters.value.reduce<StatsPredefinedMetricFilterOption[]>((acc, filter) => {
          acc.push(filter);
          return acc;
        }, []);
      }

      if (
        Object.keys(selectedChannelFiltersByCat.value).length > 0
        || Object.keys(selectedTypeFiltersByCat.value).length > 0
        || Object.keys(selectedBounceTypeFiltersByCat.value).length > 0
      ) {
        const channelsFiltersByCat: Record<string, StatsPredefinedMetricFilterOption[]> = {};
        const typesFiltersByCat: Record<string, StatsPredefinedMetricFilterOption[]> = {};
        const bouncesTypesFiltersByCat: Record<string, StatsPredefinedMetricFilterOption[]> = {};

        selectedChartCategory.value.forEach((category) => {
          const [firstTypeOfGraph] = Object.keys(category.charts);
          if (!firstTypeOfGraph) {
            return;
          }

          const firstChartFilters = category.charts[firstTypeOfGraph];
          if (!firstChartFilters) {
            return;
          }

          if (firstChartFilters.channel) {
            channelsFiltersByCat[category.code] = firstChartFilters.channel.reduce<StatsPredefinedMetricFilterOption[]>((acc, channel) => {
              acc.push(channel);
              return acc;
            }, []);
          } else if (firstChartFilters.type) {
            typesFiltersByCat[category.code] = firstChartFilters.type.reduce<StatsPredefinedMetricFilterOption[]>((acc, type) => {
              acc.push(type);
              return acc;
            }, []);
          } else if (firstChartFilters.bounce_type) {
            bouncesTypesFiltersByCat[category.code] = firstChartFilters.bounce_type.reduce<StatsPredefinedMetricFilterOption[]>((acc, type) => {
              acc.push(type);
              return acc;
            }, []);
          }
        });

        if (Object.keys(channelsFiltersByCat).length > 0) {
          selectedChannelFiltersByCat.value = channelsFiltersByCat;
        }

        if (Object.keys(typesFiltersByCat).length > 0) {
          selectedTypeFiltersByCat.value = typesFiltersByCat;
        }

        if (Object.keys(bouncesTypesFiltersByCat).length > 0) {
          selectedBounceTypeFiltersByCat.value = bouncesTypesFiltersByCat;
        }
      }
    }

    watch(selectedChartDisplayMode, (mode) => {
      if (mode !== 'timeline') {
        customizeChartTypePerCategory.value = false;
        globalChartType.value = '';
      }

      if (!mode) {
        return;
      }

      // Initialiser le type de graphique de répartition si pas défini
      if (mode === 'repartition' && !repartitionChartType.value) {
        const availableTypes = repartitionChartTypeOptions.value.map(({ key }: ChartTypeOption) => key);
        if (availableTypes.length) {
          repartitionChartType.value = availableTypes[0];
        }
      }

      setDefaultConfigurationForWidget();
    });

    function applyWidgetConfiguration() {
      if (hydratedFromConfiguration.value) {
        return;
      }

      // Si pas de configuration à restaurer, ne pas bloquer la configuration par défaut
      if (!widgetConfiguration.value) {
        return;
      }

      const configuration = widgetConfiguration.value;
      const group = metricGroups.value.find((item) => item.key === configuration.group);
      if (!group) {
        return;
      }

      const category = group.items.find((item) => item.key === configuration.category);
      if (!category) {
        return;
      }

      const metric = category.value.find((item) => item.metric === configuration.metric);
      if (!metric) {
        return;
      }

      hydratedFromConfiguration.value = true;
      selectMetricOption(metric, group.key);
      selectMetricWidgetType(configuration.widgetType);

      if (configuration.widgetType === StatsWidgetType.KPI) {
        includeTotal.value = Boolean(configuration.includeTotal);
        totalOnly.value = includeTotal.value && Boolean(configuration.totalOnly);
      } else {
        includeTotal.value = false;
        totalOnly.value = false;
      }

      if (configuration.widgetType === StatsWidgetType.CHART) {
        if (!configuration.charts || !configuration.charts.length) {
          return;
        }

        const firstConfiguredChartWithType = configuration.charts.find((chart: any) => chart.type && chart.category);
        if (firstConfiguredChartWithType?.type) {
          if (timelineChartTypes.includes(firstConfiguredChartWithType.type as ChartType)) {
            selectedChartDisplayMode.value = 'timeline';
          } else if (repartitionChartTypes.includes(firstConfiguredChartWithType.type as ChartType)) {
            selectedChartDisplayMode.value = 'repartition';
            repartitionChartType.value = firstConfiguredChartWithType.type as string;
          }
        }

        const normalizeCategoryFromConfiguration = (code: string) => normalizeLegacyCategoryCode(
          configuration.metric,
          code,
        );

        selectedChartCategory.value = listOfChartCategories.value
          .filter((item) => configuration.charts
            && configuration.charts.find((chart: any) => normalizeCategoryFromConfiguration(chart.category) === item.code));

        selectedChartCategory.value.forEach((item) => {
          const chartData = configuration.charts.find((chart: any) => normalizeCategoryFromConfiguration(chart.category) === item.code);
          if (!chartData) {
            return;
          }

          if (!chartData.type) {
            return;
          }

          selectedChartTypeByCat.value[item.code] = chartData.type;

          const chartFilters = item.charts[chartData.type] ?? {};


          // Restaurer tous les filtres dynamiques pour les graphiques (nouveaux + legacy)
          const restoredFilters: Record<string, StatsPredefinedMetricFilterOption[]> = {};
          Object.keys(chartFilters).forEach((filterKey) => {
            const savedFilterData = (chartData.filters && chartData.filters[filterKey])
              ? chartData.filters[filterKey].split(',') : [];
            const filterOptions = chartFilters[filterKey];
            const restored = restoreFilterSelections(
              filterOptions,
              savedFilterData,
            );
            restoredFilters[filterKey] = restored;
          });

          // Assigner directement l'objet complet pour garantir la réactivité
          selectedFiltersByCat.value[item.code] = restoredFilters;

        });

        // Réinitialiser le flag après que tous les watchers se soient exécutés
        nextTick(() => {
          hydratedFromConfiguration.value = false;

          // Forcer l'application du type de chart de répartition aux catégories
          if (selectedChartDisplayMode.value === 'repartition' && repartitionChartType.value) {
            selectedChartCategory.value.forEach((category) => {
              if (!selectedChartTypeByCat.value[category.code]) {
                selectedChartTypeByCat.value[category.code] = repartitionChartType.value;
              }
            });
          }
        });

        if (selectedChartDisplayMode.value === 'repartition') {
          // Lire depuis le premier chart (nouveau format)
          const firstChart = configuration.charts?.[0];
          let dimensionKey = firstChart?.repartitionDimensionKey;
          let metricKey = firstChart?.repartitionMetricKey;

          // Fallback : reconstuire à partir de la catégorie sélectionnée si pas dans le chart
          if (!dimensionKey || !metricKey) {
            const selectedVariantCode = selectedChartCategory.value[0]?.code ?? null;

            if (selectedVariantCode) {
              const matchingGroupEntry = Object.entries(chartCategoryGroups.value)
                .find(([, groupValue]) => groupValue?.variants?.includes(selectedVariantCode));

              if (matchingGroupEntry) {
                const [groupKey] = matchingGroupEntry;
                if (!dimensionKey) {
                  dimensionKey = groupKey;
                }

                const reconstructedMetricKey = getMetricKeyFromVariant(selectedVariantCode);
                if (reconstructedMetricKey && !metricKey) {
                  metricKey = reconstructedMetricKey;
                }
              }
            }
          }

          // Appliquer les valeurs restaurées
          if (dimensionKey) {
            selectedRepartitionDimensionKey.value = dimensionKey;
          }
          if (metricKey) {
            selectedRepartitionMetricKey.value = metricKey;
          }
        }

        if (selectedChartDisplayMode.value === 'timeline') {
          type ConfiguredChart = { type?: string | null };
          const configuredCharts = configuration.charts as ConfiguredChart[];
          const configuredTimelineTypes = configuredCharts.reduce((acc: Set<string>, chart: ConfiguredChart) => {
            const chartType = chart?.type;
            if (chartType && timelineChartTypes.includes(chartType as ChartType)) {
              acc.add(chartType);
            }

            return acc;
          }, new Set<string>());

          const [firstConfiguredType] = Array.from(configuredTimelineTypes);

          if (!configuredTimelineTypes.size || configuredTimelineTypes.size === 1) {
            customizeChartTypePerCategory.value = false;
            globalChartType.value = firstConfiguredType ?? '';
            synchronizeGlobalChartType();
          } else {
            customizeChartTypePerCategory.value = true;
          }
        } else {
          customizeChartTypePerCategory.value = false;
          globalChartType.value = '';
        }
      } else {
        // Restaurer tous les filtres dynamiques pour KPI et Table (nouveaux + legacy)
        const availableFilters = getAvailableFiltersForWidget();
        Object.keys(availableFilters).forEach((filterKey) => {
          const savedFilterData = (configuration.filters && configuration.filters[filterKey])
            ? configuration.filters[filterKey].split(',') : [];
          selectedFilters.value[filterKey] = restoreFilterSelections(
            availableFilters[filterKey],
            savedFilterData,
          );
        });

        // Restaurer la sélection de colonnes pour les tables
        if (configuration.widgetType === StatsWidgetType.TABLE) {
          if (configuration.selectedColumns && Array.isArray(configuration.selectedColumns)) {
            selectedTableColumns.value = configuration.selectedColumns;
          } else if (defaultTableColumns.value.length > 0) {
            // Utiliser les colonnes par défaut si aucune sélection n'est sauvegardée (rétrocompatibilité)
            selectedTableColumns.value = [...defaultTableColumns.value];
          }
        }

        // Réinitialiser le flag après que tous les watchers se soient exécutés
        nextTick(() => {
          hydratedFromConfiguration.value = false;

          // Forcer l'application du type de chart de répartition aux catégories si nécessaire
          if (selectedChartDisplayMode.value === 'repartition' && repartitionChartType.value) {
            selectedChartCategory.value.forEach((category) => {
              if (!selectedChartTypeByCat.value[category.code]) {
                selectedChartTypeByCat.value[category.code] = repartitionChartType.value;
              }
            });
          }
        });
      }
    }

    watch(metricGroups, (newGroups: StatsPredefinedMetricGroup[]) => {
      applyWidgetConfiguration();

      const visibleGroups = visibleMetricGroups.value;

      if (!visibleGroups.length) {
        if (!selectedMetric.value) {
          activeGroupKey.value = null;
        }
        return;
      }

      const selectedGroupKey = selectedMetric.value?.group;

      if (selectedGroupKey && visibleGroups.some((group) => group.key === selectedGroupKey)) {
        activeGroupKey.value = selectedGroupKey;
      } else if (!activeGroupKey.value || !visibleGroups.some((group) => group.key === activeGroupKey.value)) {
        activeGroupKey.value = visibleGroups[0].key;
      }
    }, { immediate: true, deep: true });

    // select type of chart if only 1 type is available
    watch(listOfMetricWidgetTypes, (newData) => {
      if (newData.length === 1) {
        const chartType = newData[0] as StatsWidgetType;
        selectMetricWidgetType(chartType);
      }
    });

    watch(selectedMetric, (newValue, oldValue) => {
      if (oldValue) {
        selectMetricWidgetType(null);

        // Change default widget's name value
        // widgetName.value = newValue.childrenLabel;
        if (newValue) {
          Object.keys(widgetName.value).forEach((key: string) => {
            widgetName.value[key] = newValue.childrenLabel;
          });
        }

        // Reset boolean to use default values for widget
        useDefaultConfigurationForWidget.value = true;

        // Réinitialiser les colonnes sélectionnées au changement de métrique.
        // Le watcher [selectedMetric, ..., defaultTableColumns] juste après
        // les ré-initialisera aux default_columns du nouveau metric.
        selectedTableColumns.value = [];
      } else if (newValue && Object.keys(widgetName.value).some((key) => widgetName.value[key] === '' || !widgetName.value[key])) {
        // Add default widget's name value if empty
        Object.keys(widgetName.value).forEach((key) => {
          if (widgetName.value[key] === '' || !widgetName.value[key]) {
            widgetName.value = { ...widgetName.value, [key]: newValue.childrenLabel };
          }
        });
      }
    });

    watch(canIncludeTotal, (value) => {
      if (value) {
        return;
      }

      if (includeTotal.value) {
        includeTotal.value = false;
      }

      if (totalOnly.value) {
        totalOnly.value = false;
      }
    }, { immediate: true });

    watch(showTotalOptions, (value) => {
      if (value) {
        return;
      }

      if (includeTotal.value) {
        includeTotal.value = false;
      }

      if (totalOnly.value) {
        totalOnly.value = false;
      }
    }, { immediate: true });

    watch(includeTotal, (value) => {
      if (!value && totalOnly.value) {
        totalOnly.value = false;
      }
    });

    // Initialiser les colonnes par défaut quand on sélectionne une table ou change de métrique
    watch([selectedMetric, selectedMetricWidgetType, defaultTableColumns], () => {
      if (selectedMetricWidgetType.value === StatsWidgetType.TABLE && defaultTableColumns.value.length > 0) {
        // Initialiser avec les colonnes par défaut si aucune sélection n'existe
        if (selectedTableColumns.value.length === 0) {
          selectedTableColumns.value = [...defaultTableColumns.value];
        }
      }
    }, { immediate: true });

    watch([selectedMetric, selectedMetricWidgetType, selectedChartCategory, selectedChartTypeByCat,
      selectedChannelFiltersByCat, selectedTypeFiltersByCat, selectedBounceTypeFiltersByCat,
      selectedChannelFilters, selectedTypeFilters, selectedBounceTypeFilters,
      selectedFilters, selectedFiltersByCat, selectedTableColumns, includeTotal, totalOnly, limitSlices, maxSlices,
      selectedRepartitionDimensionKey, selectedRepartitionMetricKey, customSelection,
      rawCustomSelection], () => {
      if (!selectedMetricWidgetType.value) {
        // We select the first available option
        selectMetricWidgetType(listOfMetricWidgetTypes.value[0] as StatsWidgetType);
      }

      if (useDefaultConfigurationForWidget.value) {
        setDefaultConfigurationForWidget();
        useDefaultConfigurationForWidget.value = false;
      }

      /* Format data */
      const selectedChartTypeValues = Object.values(selectedChartTypeByCat.value)
        .filter((value): value is string => !!value);
      const primaryChartType = selectedChartTypeValues[0] as ChartType | undefined;
      const formattedWidgetData: any = {
        group: selectedMetric.value ? selectedMetric.value.group : '',
        category: selectedMetric.value ? selectedMetric.value.category : '',
        metric: selectedMetric.value ? selectedMetric.value.metric : '',
        widgetType: selectedMetricWidgetType.value,
        isTimeline: primaryChartType ? [ChartType.LINE, ChartType.BAR, ChartType.SCATTER].includes(primaryChartType) : false,
      };

      if (selectedMetricWidgetType.value === StatsWidgetType.CHART) {
        formattedWidgetData.charts = selectedChartCategory.value.map((itemCategory, index) => {
          const chart: any = {
            category: itemCategory.code,
            type: selectedChartTypeByCat.value[itemCategory.code] ?? '',
          };

          if (chart.type) {
            const chartFilters = itemCategory.charts[chart.type] ?? {};
            const hasFilters = (Object.values(chartFilters) as (StatsPredefinedMetricFilterOption[] | undefined)[])
              .some((filterOptions) => !!filterOptions && filterOptions.length);
            if (hasFilters) {
              chart.filters = {};
              (Object.entries(chartFilters) as [keyof StatsPredefinedMetricFilters, StatsPredefinedMetricFilterOption[] | undefined][])
                .forEach(([key, filterOptions]) => {
                  if (filterOptions && filterOptions.length) {
                    // Système unifié pour TOUS les filtres (legacy + nouveaux)
                    const selectedFilterValues = selectedFiltersByCat.value[itemCategory.code]?.[key] || [];
                    if (selectedFilterValues.length) {
                      chart.filters[key] = selectedFilterValues
                        .map((item) => item.code)
                        .join();
                    }
                  }
                });
            }
          }

          // Stocker les dimensions de répartition dans le premier chart pour la rétrocompatibilité
          if (index === 0 && selectedChartDisplayMode.value === 'repartition') {
            if (selectedRepartitionDimensionKey.value) {
              chart.repartitionDimensionKey = selectedRepartitionDimensionKey.value;
            }
            if (selectedRepartitionMetricKey.value) {
              chart.repartitionMetricKey = selectedRepartitionMetricKey.value;
            }
          }

          return chart;
        });
      }

      if (selectedMetricWidgetType.value !== StatsWidgetType.CHART) {
        // Vérifier si des filtres existent (legacy + nouveaux filtres dynamiques)
        const availableFilters = getAvailableFiltersForWidget();
        const hasLegacyFilters = listOfChannelFilters.value.length || listOfTypeFilters.value.length || listOfBounceTypeFilters.value.length;
        const hasDynamicFilters = Object.keys(availableFilters).length > 0;

        if (hasLegacyFilters || hasDynamicFilters) {
          formattedWidgetData.filters = {};

          // Utiliser le nouveau système de filtres dynamiques pour TOUS les filtres
          Object.keys(availableFilters).forEach((filterKey) => {
            const selectedFilterValues = selectedFilters.value[filterKey] || [];
            if (selectedFilterValues.length) {
              const filterValue = selectedFilterValues.map((item) => item.code).join();
              formattedWidgetData.filters[filterKey] = filterValue;
            }
          });
        }

        // Ajouter la sélection de colonnes pour les tables
        if (selectedMetricWidgetType.value === StatsWidgetType.TABLE && selectedTableColumns.value.length > 0) {
          // Toujours inclure les colonnes obligatoires (ID/actions) même si non sélectionnées
          const allSelectedColumns = [...new Set([...selectedTableColumns.value, ...requiredTableColumns.value])];
          formattedWidgetData.selectedColumns = allSelectedColumns;
        }
      }

      if (selectedMetricWidgetType.value === StatsWidgetType.KPI && includeTotal.value) {
        formattedWidgetData.includeTotal = true;
        if (totalOnly.value) {
          formattedWidgetData.totalOnly = true;
        }
      }

      // Ajouter maxSlices pour les graphiques de répartition (seulement si limitSlices est activé)
      if (selectedMetricWidgetType.value === StatsWidgetType.CHART && selectedChartDisplayMode.value === 'repartition' && limitSlices.value) {
        formattedWidgetData.maxSlices = maxSlices.value;
      }

      // Custom KPI : injecter customConfig (qui contient measures/dimensions/chartMode/chartSubType)
      if (isCustomKpi.value) {
        formattedWidgetData.customConfig = { ...customSelection.value };
      }

      // Tableau brut sur data custom : injecter customConfig avec
      // selectedColumns/enabledRelations/userFilters/dateColumn.
      if (isRawCustomData.value) {
        formattedWidgetData.customConfig = { ...rawCustomSelection.value };
      }

      widgetConfiguration.value = formattedWidgetData;
    }, { deep: true });

    watch([widgetName, widgetConfiguration], () => {
      let updatedWidgetName;
      if (typeof widgetName.value === 'string') {
        updatedWidgetName = {
          [locale.value]: widgetName.value,
        };
      } else {
        updatedWidgetName = { ...widgetName.value };
      }

      const updatedModelValue = {
        ...props.modelValue,
        widgetName: updatedWidgetName,
        widgetConfiguration: widgetConfiguration.value,
      };

      context.emit('update:modelValue', WidgetDataMetadata.Create(updatedModelValue));
    }, { deep: true });

    return {
      t,
      te,
      formValidationErrors,
      componentFieldErrorsKey,
      widgetName,
      metricSearchTerm,
      isMetricPickerOpen,
      openMetricPicker,
      closeMetricPicker,
      confirmMetricSelection,
      metricGroups,
      visibleMetricGroups,
      groupMetricsCount,
      activeGroupKey,
      activeGroupCategories,
      filteredActiveGroupCategories,
      flatCustomMetrics,
      getMetricDescription,
      // Type filters (chips)
      availableMetricTypeFilters,
      metricTypeFilters,
      isMetricTypeFilterActive,
      toggleMetricTypeFilter,
      resetMetricTypeFilters,
      isSearching,
      searchResults,
      selectGroup,
      selectMetricOption,
      isMetricSelected,
      selectedMetric,
      selectedMetricContext,
      getMetricAvailableTypes,
      getMetricTypeLabel,
      getMetricTypeIcon,

      listOfMetricWidgetTypes,
      selectedMetricWidgetType,
      selectMetricWidgetType,

      chartDisplayModeOptions,
      selectedChartDisplayMode,
      selectChartDisplayMode,
      availableChartCategories,
      isRepartitionMode,
      hasRepartitionGroups,
      chartCategoriesLabel,
      chartCategoriesPlaceholder,
      repartitionDimensionOptions,
      repartitionMetricOptions,
      selectedRepartitionDimensionKey,
      selectedRepartitionMetricKey,
      repartitionMetricLabel,
      repartitionMetricPlaceholder,
      shouldUseRepartitionMetricSelector,
      repartitionChartTypeOptions,
      repartitionChartType,
      selectRepartitionChartType,
      limitSlices,
      maxSlices,

      listOfChartCategories,
      listOfChartCategoriesIsMultiple,
      selectedChartCategory,
      selectedChartTypeByCat,
      customizeChartTypePerCategory,
      globalChartType,
      globalTimelineChartTypeOptions,
      globalTimelineFilterOptions,
      globalTimelineFiltersSelection,
      shouldShowTimelineTypeToggle,
      shouldShowGlobalChartTypeSelector,
      shouldShowGlobalTimelineFilters,
      selectChartTypeByCategory,
      selectGlobalChartType,
      selectedChannelFiltersByCat,
      selectedTypeFiltersByCat,
      selectedBounceTypeFiltersByCat,

      listOfChannelFilters,
      selectedChannelFilters,

      listOfTypeFilters,
      selectedTypeFilters,

      listOfBounceTypeFilters,
      selectedBounceTypeFilters,

      includeTotal,
      totalOnly,
      canIncludeTotal,
      showTotalOptions,

      // Custom KPI : exposition au template
      isCustomKpi,
      customMeta,
      customMeasuresOptions,
      customDimensionsOptions,
      customSupportsTimeline,
      customSelection,
      onCustomSelectionUpdate,

      // Tableau brut sur data custom : exposition au template
      isRawCustomData,
      rawCustomMeta,
      rawCustomSelection,
      onRawCustomSelectionUpdate,
      isRawCustomDataMetric,
      kpiCustomEntries,
      rawCustomEntries,
      HIDE_CUSTOM_KPI_SECTION,

      // Nouvelles fonctions pour le système de filtres généralisé
      selectedFilters,
      selectedFiltersByCat,
      getFilterLabel,
      getFilterPlaceholder,
      getAvailableFiltersForChart,
      getAvailableFiltersForWidget,

      // Système de sélection de colonnes pour les tables
      availableTableColumns,
      availableTableColumnsGroups,
      selectedTableColumns,
      requiredTableColumns,

      chartConfigPanel,
      chartSubConfigPanels,
      visibleChartSubConfigPanels,
      defaultLanguages,

      // Enums pour le template
      StatsWidgetType,
    };
  },
});
